import React, { useState, useEffect, useCallback } from "react";
import {
    LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";

const API_BASE = "https://rhoulamikhadija.com/qos_api";

/* ─────────────────────────────────────────────────────────
   Design tokens
   ───────────────────────────────────────────────────────── */
const T = {
    navy: "#0B2545", navy700: "#123055", navy600: "#1C3E68",
    accent: "#27ccb9", accentSoft: "rgba(45,212,191,.15)",
    red: "#db2029", green: "#32cd8f", amber: "#c3c32a", blue: "#33b5d8", purple: "#ca77f0",
    bg: "#F1F4F9", card: "#FFFFFF", cardAlt: "#F5F7FB", sidebar: "#0B2545",
    line: "#E4E9F1", lineSoft: "#EEF2F8",
    ink: "#101828", sub: "#5B6472", faint: "#8A93A3",
    sans: "'Manrope',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
    mono: "'JetBrains Mono','SFMono-Regular',Menlo,monospace",
};

const OP_COLOR = {
    "Opérateur 1": "#F36A21",
    "Opérateur 2": "#0066B3",
    "Opérateur 3": "#7A1FA2",
};
const opColor = (n) => OP_COLOR[n] || T.navy;

const CHART_GRID = "#E6ECF4";
const CHART_AXIS = "#8A93A3";

/* fonts + keyframes, injected once */
function ensureAssets() {
    if (typeof document === "undefined") return;
    if (!document.getElementById("__qos_fonts")) {
        const l = document.createElement("link");
        l.id = "__qos_fonts"; l.rel = "stylesheet";
        l.href = "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap";
        document.head.appendChild(l);
    }
    if (!document.getElementById("__qos_kf")) {
        const s = document.createElement("style");
        s.id = "__qos_kf";
        s.textContent = "@keyframes qspin{to{transform:rotate(360deg)}}";
        document.head.appendChild(s);
    }
}

/* Charge Leaflet (CSS + JS) une seule fois et résout quand window.L est prêt */
function ensureLeaflet() {
    return new Promise((resolve) => {
        if (typeof window === "undefined") return resolve(null);
        if (window.L) return resolve(window.L);
        if (!document.getElementById("__leaflet_css")) {
            const c = document.createElement("link");
            c.id = "__leaflet_css"; c.rel = "stylesheet";
            c.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
            document.head.appendChild(c);
        }
        let s = document.getElementById("__leaflet_js");
        if (!s) {
            s = document.createElement("script");
            s.id = "__leaflet_js"; s.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
            document.head.appendChild(s);
        }
        if (window.L) return resolve(window.L);
        s.addEventListener("load", () => resolve(window.L));
        const iv = setInterval(() => { if (window.L) { clearInterval(iv); resolve(window.L); } }, 120);
    });
}

/* ─────────────────────────────────────────────────────────
   Icons
   ───────────────────────────────────────────────────────── */
const Ico = {
    dash: "M3 13h8V3H3z M13 21h8V3h-8z M3 21h8v-6H3z",
    chart: "M3 3v16a2 2 0 0 0 2 2h16 M7 14l3-3 3 3 4-5",
    table: "M3 3h18v18H3z M3 9h18 M3 15h18 M9 3v18",
    download: "M12 3v12 M7 10l5 5 5-5 M5 21h14",
    bell: "M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9 M13.7 21a2 2 0 0 1-3.4 0",
    folders: "M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7l-2-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z",
    etl: "M12 20a8 8 0 1 0-8-8 M12 4v4l3 2 M12 2a10 10 0 0 1 0 20",
    signal: "M2 20h.01 M7 20v-4 M12 20v-8 M17 20V8 M22 20V4",
    lock: "M5 11h14v10H5z M8 11V7a4 4 0 0 1 8 0v4",
    logout: "M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4 M16 17l5-5-5-5 M21 12H9",
    refresh: "M21 12a9 9 0 1 1-3-6.7L21 8 M21 3v5h-5",
    play: "M6 4l14 8-14 8z",
    chevL: "M15 18l-6-6 6-6",
    chevR: "M9 18l6-6-6-6",
    clock: "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 6v6l4 2",
    map: "M9 3 3 6v15l6-3 6 3 6-3V3l-6 3-6-3z M9 3v15 M15 6v15",
    layers: "M12 2 2 7l10 5 10-5-10-5z M2 17l10 5 10-5 M2 12l10 5 10-5",
    flag: "M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z M4 22v-7",
    search: "M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z M21 21l-4.3-4.3",
    check: "M20 6 9 17l-5-5",
    warn: "M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z M12 9v4 M12 17h.01",
    down: "M3 17l6-6 4 4 8-8 M21 7v6h-6",
    arrowUp: "M12 19V5 M6 11l6-6 6 6",
    arrowDown: "M12 5v14 M6 13l6 6 6-6",
    dots: "M12 5h.01 M12 12h.01 M12 19h.01",
    pin: "M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11z M12 10a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z",
    plus: "M12 5v14 M5 12h14",
    edit: "M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5z",
    upload: "M12 3v12 M7 8l5-5 5 5 M5 21h14",
    gear: "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.26.604.852 1 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z",
    x: "M18 6 6 18 M6 6l12 12",
    trash: "M3 6h18 M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2 M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6",
    list: "M8 6h13 M8 12h13 M8 18h13 M3 6h.01 M3 12h.01 M3 18h.01",
    scale: "M12 3v18 M6 6h12 M4 6l2 6a2 2 0 0 0 4 0l-2-6 M14 6l2 6a2 2 0 0 0 4 0l-2-6",
};
function Icon({ d, size = 18, color = "currentColor", w = 1.9 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
            stroke={color} strokeWidth={w} strokeLinecap="round" strokeLinejoin="round">
            {d.split(" M").map((seg, i) => <path key={i} d={i === 0 ? seg : "M" + seg} />)}
        </svg>
    );
}

/* ─────────────────────────────────────────────────────────
   Token hook + fetch
   ───────────────────────────────────────────────────────── */
function useToken() {
    const [token, setToken] = useState(() => sessionStorage.getItem("admin_token") || "");
    const save = (t) => { sessionStorage.setItem("admin_token", t); setToken(t); };
    const clear = () => { sessionStorage.removeItem("admin_token"); setToken(""); };
    return [token, save, clear];
}

async function apiFetch(path, token, options = {}) {
    const res = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            ...(options.headers || {}),
        },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
}

/* ─────────────────────────────────────────────────────────
   UI primitives
   ───────────────────────────────────────────────────────── */
const Card = ({ children, style, onClick }) => (
    <div onClick={onClick} style={{
        cursor: onClick ? "pointer" : "default",
        background: T.card, border: `1px solid ${T.line}`, borderRadius: 14,
        boxShadow: "0 1px 2px rgba(16,24,40,.04),0 1px 3px rgba(16,24,40,.06)", ...style,
    }}>{children}</div>
);

const SectionTitle = ({ children, right }) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 3, height: 15, background: T.red, borderRadius: 2 }} />
            <span style={{ fontSize: 12.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".05em", color: T.ink }}>{children}</span>
        </div>
        {right}
    </div>
);

const Btn = ({ children, onClick, color = T.navy, variant = "solid", small, disabled, loading }) => {
    const styles = {
        solid: { background: color, color: "#fff", border: `1px solid ${color}` },
        outline: { background: "transparent", color: color === T.navy ? T.accent : color, border: `1px solid ${color === T.navy ? T.accent : color}55` },
        ghost: { background: "transparent", color, border: "1px solid transparent" },
    }[variant];
    return (
        <button onClick={onClick} disabled={disabled || loading} style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
            padding: small ? "7px 13px" : "11px 18px", borderRadius: 9,
            cursor: disabled || loading ? "not-allowed" : "pointer",
            fontSize: small ? 12.5 : 13.5, fontWeight: 700, fontFamily: T.sans,
            opacity: disabled || loading ? 0.55 : 1, transition: "filter .15s", ...styles,
        }}>
            {loading && <span style={{
                width: 12, height: 12, border: "2px solid currentColor", borderTopColor: "transparent",
                borderRadius: "50%", display: "inline-block", animation: "qspin .8s linear infinite",
            }} />}
            {children}
        </button>
    );
};

const Badge = ({ children, color }) => (
    <span style={{
        display: "inline-flex", alignItems: "center", height: 20, fontSize: 10.5, fontWeight: 700,
        letterSpacing: ".04em", padding: "0 8px", borderRadius: 999, fontFamily: T.sans,
        background: `${color}14`, color, border: `1px solid ${color}33`,
    }}>{children}</span>
);

const Alert = ({ type, children }) => {
    const c = { success: T.green, error: T.red, warning: T.amber }[type] || T.sub;
    const ic = { success: Ico.check, error: Ico.warn, warning: Ico.warn }[type] || Ico.warn;
    return (
        <div style={{
            display: "flex", gap: 10, alignItems: "flex-start", padding: "12px 15px", borderRadius: 11,
            background: `${c}10`, border: `1px solid ${c}33`, color: c, fontSize: 13, fontWeight: 600,
        }}>
            <span style={{ flexShrink: 0, marginTop: 1 }}><Icon d={ic} size={16} color={c} /></span>
            <div style={{ minWidth: 0 }}>{children}</div>
        </div>
    );
};

const Loading = ({ label = "Chargement…" }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "26px 4px", color: T.faint, fontSize: 13.5, fontFamily: T.sans }}>
        <span style={{ width: 15, height: 15, borderRadius: "50%", border: `2px solid ${T.line}`, borderTopColor: T.blue, animation: "qspin .7s linear infinite", display: "inline-block" }} />
        {label}
    </div>
);

const Field = ({ icon, ...props }) => (
    <div style={{ position: "relative", flex: props.flex, minWidth: props.minWidth }}>
        {icon && <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: T.faint }}><Icon d={icon} size={15} /></span>}
        <input {...props} style={{
            width: "100%", padding: icon ? "10px 13px 10px 34px" : "10px 13px", borderRadius: 10,
            border: `1.5px solid ${T.line}`, fontSize: 13.5, boxSizing: "border-box", outline: "none",
            fontFamily: T.sans, color: T.ink, background: T.cardAlt,
        }} />
    </div>
);

/* ─────────────────────────────────────────────────────────
   Login
   ───────────────────────────────────────────────────────── */
function LoginPage({ onLogin }) {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [focus, setFocus] = useState("");
    useEffect(ensureAssets, []);

    async function handleLogin() {
        setLoading(true); setError("");
        try {
            const res = await fetch(`${API_BASE}/api/admin/login`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ username, password }),
            });
            const data = await res.json();
            if (data.success) onLogin(data.token);
            else setError(data.error || "Identifiants incorrects");
        } catch { setError("Impossible de joindre le serveur"); }
        setLoading(false);
    }

    const field = (n) => ({
        width: "100%", padding: "11px 13px", borderRadius: 10, fontSize: 14, fontFamily: T.sans,
        boxSizing: "border-box", outline: "none", color: T.ink, background: T.cardAlt,
        border: `1.5px solid ${focus === n ? T.accent : T.line}`,
        boxShadow: focus === n ? `0 0 0 3px ${T.accent}22` : "none", transition: "border-color .15s,box-shadow .15s",
    });

    return (
        <div style={{ minHeight: "100vh", fontFamily: T.sans, display: "grid", gridTemplateColumns: "1.05fr 1fr", background: T.bg }}>
            <div style={{
                background: `radial-gradient(120% 120% at 0% 0%, ${T.navy600} 0%, ${T.navy} 55%)`,
                color: "#fff", padding: "56px 52px", display: "flex", flexDirection: "column",
                justifyContent: "space-between", position: "relative", overflow: "hidden",
            }}>
                <div style={{
                    position: "absolute", inset: 0, opacity: .5,
                    backgroundImage: `linear-gradient(${T.navy600}55 1px,transparent 1px),linear-gradient(90deg,${T.navy600}55 1px,transparent 1px)`,
                    backgroundSize: "34px 34px", maskImage: "radial-gradient(80% 80% at 30% 20%,#000,transparent)"
                }} />
                <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 11, background: "#fff2", border: "1px solid #ffffff2e", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Icon d={Ico.signal} size={20} color="#fff" />
                    </div>
                    <div style={{ fontWeight: 800, fontSize: 15 }}>Plateforme QoS</div>
                </div>
                <div style={{ position: "relative" }}>
                    <div style={{ fontSize: 34, fontWeight: 800, lineHeight: 1.12, letterSpacing: "-.02em" }}>Console<br />d'administration</div>
                    <div style={{ marginTop: 14, fontSize: 14.5, color: "#B7C6DE", maxWidth: 370, lineHeight: 1.6 }}>
                        Supervision des mesures de qualité de service 4G / 5G — statistiques, graphiques, alertes et pipeline ETL.
                    </div>
                </div>
                <div style={{ position: "relative", display: "flex", gap: 20, fontSize: 12.5, color: "#8FA3C2", fontFamily: T.mono }}>
                    <span>Plateforme d'évaluation de la qualité de service vocal 4G/5G </span><span>·</span><span>© 2026</span>
                </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 32 }}>
                <div style={{ width: 340 }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: T.ink, letterSpacing: "-.01em" }}>Connexion</div>
                    <div style={{ fontSize: 13.5, color: T.sub, marginTop: 5 }}>Accès réservé à l'administrateur.</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 15, marginTop: 26 }}>
                        <div>
                            <label style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: ".04em", color: T.sub, display: "block", marginBottom: 6 }}>IDENTIFIANT</label>
                            <input value={username} placeholder="admin" onChange={(e) => setUsername(e.target.value)}
                                onFocus={() => setFocus("u")} onBlur={() => setFocus("")} style={field("u")} />
                        </div>
                        <div>
                            <label style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: ".04em", color: T.sub, display: "block", marginBottom: 6 }}>MOT DE PASSE</label>
                            <input type="password" value={password} placeholder="••••••••" onChange={(e) => setPassword(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                                onFocus={() => setFocus("p")} onBlur={() => setFocus("")} style={field("p")} />
                        </div>
                        {error && <Alert type="error">{error}</Alert>}
                        <div style={{ marginTop: 4 }}>
                            <Btn onClick={handleLogin} loading={loading} disabled={!username || !password} color={T.navy}>
                                <Icon d={Ico.lock} size={16} color="#fff" />{loading ? "Connexion…" : "Se connecter"}
                            </Btn>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ─────────────────────────────────────────────────────────
   1. Tableau de bord
   ───────────────────────────────────────────────────────── */
/* KPI tile — reference style: label top-left, icon top-right, big value, delta line */
function KpiTile({ label, value, icon, color, delta, deltaDir, foot }) {
    const dc = deltaDir === "down" ? T.red : deltaDir === "up" ? T.green : T.sub;
    return (
        <div style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: 16, padding: "18px 20px", boxShadow: "0 1px 2px rgba(16,24,40,.04)" }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.sub }}>{label}</span>
                <span style={{ width: 34, height: 34, borderRadius: 10, background: `${color}12`, color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon d={icon} size={17} color={color} />
                </span>
            </div>
            <div style={{ fontSize: 30, fontWeight: 800, color: T.ink, letterSpacing: "-.02em", lineHeight: 1.1, marginTop: 14 }}>{value}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                <span style={{ fontSize: 12, color: T.faint }}>{foot}</span>
                {delta != null && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, marginLeft: "auto", fontSize: 12.5, fontWeight: 800, color: dc }}>
                        {deltaDir && <Icon d={deltaDir === "down" ? Ico.arrowDown : Ico.arrowUp} size={13} color={dc} w={2.4} />}{delta}
                    </span>
                )}
            </div>
        </div>
    );
}

function MiniStat({ icon, color, title, children }) {
    return (
        <Card style={{ padding: "16px 18px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 12 }}>
                <span style={{ width: 30, height: 30, borderRadius: 9, background: `${color}12`, color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon d={icon} size={16} color={color} />
                </span>
                <span style={{ fontSize: 11, fontWeight: 700, color: T.faint, textTransform: "uppercase", letterSpacing: ".05em" }}>{title}</span>
            </div>
            {children}
        </Card>
    );
}

function SectionStats({ token }) {
    const [stats, setStats] = useState(null);
    const [derniere, setDerniere] = useState(null);
    const [kpi, setKpi] = useState(null);
    const [anom, setAnom] = useState(null);
    const [evol, setEvol] = useState([]);
    const [etl, setEtl] = useState(null);
    const [services, setServices] = useState([]);

    useEffect(() => {
        Promise.all([
            apiFetch("/api/admin/stats", token),
            apiFetch("/api/admin/mesures?page=1&per_page=1", token),
            apiFetch("/api/kpi/reussite", token),
            apiFetch("/api/ml/anomalies", token),
            apiFetch("/api/campagnes", token),
            apiFetch("/api/admin/etl-log", token).catch(() => null),
        ]).then(([s, m, k, a, camp, log]) => {
            setStats(s); setDerniere(m.mesures?.[0] || null); setKpi(k); setAnom(a); setEtl(log);
            setEvol((camp.campagnes || []).map((c, i) => ({ x: `C${i + 1}`, date: c.date_mesure, v: c.nb_mesures })));
        }).catch(() => { });
        apiFetch("/api/kpi/filtre", token).then((d) => {
            const rows = d.results || [], svc = {};
            rows.forEach((r) => { const s = r.service || "4G"; svc[s] = (svc[s] || 0) + (r.total_mesures || 0); });
            setServices(Object.entries(svc).map(([label, value]) => ({ label, value })));
        }).catch(() => { });
    }, [token]);

    if (!stats) return <Loading />;

    const tmr = kpi?.results?.[0]?.tmr ?? null;
    const meilleur = [...(kpi?.results || [])].sort((a, b) => (b.tr_pct || 0) - (a.tr_pct || 0))[0];
    const nbAnom = anom?.nb_anomalies || 0;
    const totalM = anom?.total_mesures || stats.total_mesures || 0;
    const anomRate = totalM > 0 ? Math.round((nbAnom / totalM) * 100) : 0;
    const progression = Math.min(Math.round((stats.total_mesures / 100) * 100), 100);

    let etlStatut = { label: "Non exécuté", color: T.faint, dot: T.faint };
    if (etl?.contenu) {
        const last = etl.contenu.split("\n").filter(Boolean).slice(-5).join(" ");
        if (/succès|terminé/.test(last)) etlStatut = { label: "Dernier run OK", color: T.green, dot: T.green };
        else if (/ERREUR|Error/.test(last)) etlStatut = { label: "Erreur détectée", color: T.red, dot: T.red };
        else etlStatut = { label: "Log disponible", color: T.amber, dot: T.amber };
    }
    const svcColor = (l) => (l === "5G" ? T.green : l === "4G" ? T.accent : T.amber);

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {stats.nb_campagnes === 0 && <Alert type="warning">Aucune campagne enregistrée.</Alert>}

            {/* KPI row — reference style */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14 }}>
                <KpiTile label="Mesures totales" value={stats.total_mesures} icon={Ico.table} color={T.accent}
                    foot="vers 100 mesures" delta={`${progression}%`} deltaDir="up" />
                <KpiTile label="TMR global" value={tmr != null ? `${Number(tmr).toFixed(1)}%` : "—"} icon={Ico.chart} color={T.green}
                    foot={meilleur ? `Top : ${meilleur.operateur}` : "Qualité réseau"} />
                <KpiTile label="Opérateurs" value={stats.nb_operateurs} icon={Ico.layers} color={T.blue}
                    foot={`${stats.nb_villes} villes · ${stats.nb_campagnes} campagnes`} />
                <KpiTile label="Anomalies ML" value={`${nbAnom} / ${totalM}`} icon={Ico.warn} color={nbAnom > 0 ? T.red : T.green}
                    foot="du total détecté" delta={`${anomRate}%`} deltaDir={nbAnom > 0 ? "down" : "up"} />
            </div>

            {/* Hero (sparkline + progression) + mini stat grid — reference image 2 */}
            <div style={{ display: "grid", gridTemplateColumns: "1.15fr 1fr", gap: 14 }}>
                <Card style={{ padding: 22, display: "flex", flexDirection: "column" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                        <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: T.sub }}>Progression de la collecte</div>
                            <div style={{ fontSize: 34, fontWeight: 800, color: T.ink, letterSpacing: "-.02em", marginTop: 6 }}>
                                {stats.total_mesures}<span style={{ fontSize: 15, fontWeight: 700, color: T.faint }}> / 100 mesures</span>
                            </div>
                            <div style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 8, fontSize: 12.5, fontWeight: 800, color: T.green }}>
                                <Icon d={Ico.arrowUp} size={13} color={T.green} w={2.4} />{progression}% <span style={{ color: T.faint, fontWeight: 500 }}>de l'objectif</span>
                            </div>
                        </div>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 700, color: etlStatut.color, background: `${etlStatut.color}12`, padding: "6px 11px", borderRadius: 999 }}>
                            <span style={{ width: 7, height: 7, borderRadius: "50%", background: etlStatut.dot }} />{etlStatut.label}
                        </span>
                    </div>

                    <div style={{ margin: "18px 0 4px", flex: 1, minHeight: 96 }}>
                        <ResponsiveContainer width="100%" height={110}>
                            <AreaChart data={evol} margin={{ top: 6, right: 4, bottom: 0, left: 4 }}>
                                <defs>
                                    <linearGradient id="gHero" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={T.accent} stopOpacity={.30} />
                                        <stop offset="100%" stopColor={T.accent} stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <Tooltip {...tooltipStyle} formatter={(v) => [v, "Mesures"]}
                                    labelFormatter={(l) => { const d = evol.find((x) => x.x === l); return d ? `${l} — ${d.date}` : l; }} />
                                <Area isAnimationActive={false} type="monotone" dataKey="v" stroke={T.accent} strokeWidth={2.5} fill="url(#gHero)" dot={false} activeDot={{ r: 5, fill: T.accent }} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>

                    <div style={{ height: 8, background: T.lineSoft, borderRadius: 999, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${progression}%`, background: progression >= 100 ? T.green : T.accent, borderRadius: 999, transition: "width .5s ease" }} />
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, fontSize: 12, color: T.sub }}>
                        <span>{stats.premiere_campagne || "—"}</span>
                        <span style={{ color: T.faint }}>Période de collecte</span>
                        <span>{stats.derniere_campagne || "—"}</span>
                    </div>
                </Card>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr", gap: 14 }}>
                    {meilleur && (
                        <MiniStat icon={Ico.flag} color={opColor(meilleur.operateur)} title="Meilleur opérateur">
                            <div style={{ fontSize: 17, fontWeight: 800, color: opColor(meilleur.operateur) }}>{meilleur.operateur}</div>
                            <div style={{ fontSize: 12.5, color: T.sub, marginTop: 3 }}>TR% <strong style={{ color: T.green, fontFamily: T.mono }}>{meilleur.tr_pct}%</strong> · signe <strong style={{ fontFamily: T.mono }}>{meilleur.signe}</strong></div>
                        </MiniStat>
                    )}
                    <MiniStat icon={Ico.etl} color={etlStatut.color} title="Pipeline ETL">
                        <div style={{ fontSize: 14.5, fontWeight: 800, color: etlStatut.color }}>{etlStatut.label}</div>
                        <div style={{ fontSize: 12, color: T.faint, marginTop: 4 }}>Cron planifié · 00:00 chaque nuit</div>
                    </MiniStat>
                    {derniere && (
                        <MiniStat icon={Ico.clock} color={T.green} title="Dernière mesure">
                            <div style={{ fontSize: 13.5, color: T.ink }}>
                                <strong style={{ fontFamily: T.mono }}>#{derniere.mesure}</strong> · <strong style={{ color: opColor(derniere.operateur) }}>{derniere.operateur}</strong>
                            </div>
                            <div style={{ fontSize: 12, color: T.sub, marginTop: 4 }}>{derniere.site_emission} · <span style={{ fontFamily: T.mono }}>{derniere.duree}s</span></div>
                        </MiniStat>
                    )}
                    <MiniStat icon={Ico.signal} color={T.blue} title="Répartition réseau">
                        {services.length > 0 ? services.slice(0, 3).map((s) => {
                            const pct = totalM > 0 ? Math.round((s.value / totalM) * 100) : 0;
                            return (
                                <div key={s.label} style={{ marginBottom: 7 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, marginBottom: 3 }}>
                                        <span style={{ fontWeight: 700, color: T.ink }}>{s.label}</span><span style={{ color: T.faint, fontFamily: T.mono }}>{pct}%</span>
                                    </div>
                                    <div style={{ height: 5, background: T.lineSoft, borderRadius: 3 }}>
                                        <div style={{ height: "100%", width: `${pct}%`, background: svcColor(s.label), borderRadius: 3 }} />
                                    </div>
                                </div>
                            );
                        }) : <div style={{ fontSize: 12, color: T.faint }}>—</div>}
                    </MiniStat>
                </div>
            </div>

            {/* Suivi des opérateurs — barème comparatif (3 vues) */}
            <SuiviOperateurs token={token} />
        </div>
    );
}

/* ─────────────────────────────────────────────────────────
   1bis. Suivi opérateurs — barème comparatif (3 sous-vues)
   ───────────────────────────────────────────────────────── */
const BAREME = [
    { signe: "+++", label: "Très supérieur", color: "#0E7A4E", regle: "ratio > 1.05" },
    { signe: "++", label: "Supérieur", color: "#15A97C", regle: "1.02 < ratio ≤ 1.05" },
    { signe: "+", label: "Légèrement supérieur", color: "#4FB98C", regle: "1.01 < ratio ≤ 1.02" },
    { signe: "=", label: "Dans la moyenne", color: "#E0A82E", regle: "0.99 ≤ ratio ≤ 1.01" },
    { signe: "−", label: "Légèrement inférieur", color: "#DE9152", regle: "0.98 ≤ ratio < 0.99" },
    { signe: "−−", label: "Inférieur", color: "#D6674A", regle: "0.95 ≤ ratio < 0.98" },
    { signe: "−−−", label: "Très inférieur", color: "#C0392F", regle: "ratio < 0.95" },
];
const signeColor = (s) => BAREME.find((b) => b.signe === s)?.color || T.faint;
const signeLabel = (s) => BAREME.find((b) => b.signe === s)?.label || "—";
function statutBadge(signe) {
    const bon = ["+++", "++", "+", "="].includes(signe);
    const crit = ["−−−", "−−"].includes(signe);
    return bon ? { label: "Bon", color: T.green } : crit ? { label: "Critique", color: T.red } : { label: "Attention", color: T.amber };
}

function SuiviOperateurs({ token }) {
    const [kpi, setKpi] = useState(null);
    const [exporting, setExporting] = useState(null);
    const [tab, setTab] = useState("stats");

    useEffect(() => { apiFetch("/api/kpi/reussite", token).then(setKpi).catch(() => { }); }, [token]);

    async function exporterOperateur(operateur) {
        setExporting(operateur);
        try {
            const data = await apiFetch("/api/admin/mesures?page=1&per_page=9999", token);
            const mesures = (data.mesures || []).filter((m) => m.operateur === operateur);
            if (!mesures.length) return;
            const cols = ["id", "mesure", "service", "operateur", "site_emission", "duree", "quality_pesq", "signal_strength", "date_mesure"];
            const csv = [cols.join(";")].concat(mesures.map((m) => cols.map((c) => `"${m[c] ?? ""}"`).join(";"))).join("\n");
            const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob); const a = document.createElement("a");
            a.href = url; a.download = `qos_${operateur.replace(/\s/g, "_")}_${new Date().toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(url);
        } catch { } finally { setExporting(null); }
    }

    const rows = kpi?.results || [];
    const tmr = rows[0]?.tmr ?? 0;
    const rowsTries = [...rows].sort((a, b) => (b.tr_pct || 0) - (a.tr_pct || 0));

    const tabs2 = [
        { id: "stats", label: "Suivi opérateurs", icon: Ico.list },
        { id: "comparaison", label: "Comparaison visuelle", icon: Ico.chart },
        { id: "bareme", label: "Barème", icon: Ico.scale },
    ];

    return (
        <Card style={{ padding: "20px 22px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ width: 3, height: 15, background: T.red, borderRadius: 2 }} />
                    <span style={{ fontSize: 12.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".05em", color: T.ink }}>Suivi des opérateurs — barème comparatif</span>
                </div>
                {tmr > 0 && <span style={{ fontSize: 12.5, color: T.sub }}>TMR global : <strong style={{ color: T.ink, fontFamily: T.mono }}>{tmr.toFixed(1)}%</strong></span>}
            </div>

            <div style={{ display: "flex", gap: 3, padding: 3, borderRadius: 10, background: T.lineSoft, marginBottom: 18, width: "fit-content" }}>
                {tabs2.map((t) => (
                    <button key={t.id} onClick={() => setTab(t.id)} style={{
                        display: "flex", alignItems: "center", gap: 7, padding: "8px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                        fontFamily: T.sans, fontSize: 12.5, fontWeight: 700, background: tab === t.id ? T.card : "transparent",
                        color: tab === t.id ? T.navy : T.sub, boxShadow: tab === t.id ? "0 1px 2px rgba(16,24,40,.12)" : "none", transition: "all .15s",
                    }}><Icon d={t.icon} size={14} color={tab === t.id ? T.navy : T.sub} />{t.label}</button>
                ))}
            </div>

            {tab === "stats" && (
                <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.sans, fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: T.navy }}>
                                {["Opérateur", "Mesures", "TE%", "TC%", "TR%", "TMR%", "Signe", "Statut", "Actions"].map((h) => (
                                    <th key={h} style={{ padding: "10px 12px", color: "#fff", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", textAlign: "left" }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rowsTries.map((r, i) => {
                                const st = statutBadge(r.signe); const sc = signeColor(r.signe);
                                return (
                                    <tr key={r.operateur} style={{ background: i % 2 === 0 ? T.card : T.cardAlt, borderBottom: `1px solid ${T.lineSoft}` }}>
                                        <td style={{ padding: "11px 12px" }}>
                                            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                                                <span style={{ width: 9, height: 9, borderRadius: "50%", background: opColor(r.operateur), flexShrink: 0 }} />
                                                <span style={{ fontWeight: 700, color: T.ink }}>{r.operateur}</span>
                                            </span>
                                        </td>
                                        <td style={{ padding: "11px 12px", fontFamily: T.mono, color: T.sub }}>{r.total_mesures}</td>
                                        <td style={{ padding: "11px 12px", fontFamily: T.mono, fontWeight: r.te_pct > 5 ? 700 : 400, color: r.te_pct > 5 ? T.red : T.ink }}>{r.te_pct}%</td>
                                        <td style={{ padding: "11px 12px", fontFamily: T.mono, color: T.amber }}>{r.tc_pct}%</td>
                                        <td style={{ padding: "11px 12px", fontFamily: T.mono, fontWeight: 700, color: r.tr_pct >= 95 ? T.green : r.tr_pct < 50 ? T.red : T.ink }}>{r.tr_pct}%</td>
                                        <td style={{ padding: "11px 12px", fontFamily: T.mono, color: T.sub }}>{r.tmr}%</td>
                                        <td style={{ padding: "11px 12px" }}><span style={{ fontFamily: T.mono, fontSize: 15, fontWeight: 700, color: sc }}>{r.signe}</span></td>
                                        <td style={{ padding: "11px 12px" }}><Badge color={st.color}>{st.label}</Badge></td>
                                        <td style={{ padding: "11px 12px" }}>
                                            <div style={{ display: "flex", gap: 6 }}>
                                                <Btn small variant="outline" color={T.navy} onClick={() => window.open(`https://qos.rhoulamikhadija.com?operateur=${encodeURIComponent(r.operateur)}`, "_blank")}><Icon d={Ico.dots} size={13} />Voir</Btn>
                                                <Btn small variant="outline" color={T.green} loading={exporting === r.operateur} onClick={() => exporterOperateur(r.operateur)}><Icon d={Ico.download} size={13} />CSV</Btn>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === "comparaison" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                    {rowsTries.map((r, i) => {
                        const sc = signeColor(r.signe);
                        return (
                            <div key={r.operateur}>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                        <span style={{ fontWeight: 700, color: T.ink, fontSize: 14 }}>{i + 1}. {r.operateur}</span>
                                        <span style={{ fontFamily: T.mono, fontSize: 14, fontWeight: 700, color: sc }}>{r.signe}</span>
                                        <span style={{ fontSize: 11.5, color: T.sub }}>{signeLabel(r.signe)}</span>
                                    </div>
                                    <span style={{ fontFamily: T.mono, fontWeight: 800, fontSize: 15, color: sc }}>{r.tr_pct}%</span>
                                </div>
                                <div style={{ position: "relative", height: 28, background: T.lineSoft, borderRadius: 7, overflow: "visible" }}>
                                    <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${r.tr_pct}%`, background: `linear-gradient(90deg, ${sc}CC, ${sc})`, borderRadius: 7, transition: "width .6s ease", display: "flex", alignItems: "center", paddingLeft: 10, overflow: "hidden" }}>
                                        <span style={{ fontSize: 11, fontWeight: 700, color: "#fff", whiteSpace: "nowrap" }}>TR : {r.tr_pct}%</span>
                                    </div>
                                    <div style={{ position: "absolute", top: -18, bottom: 0, left: `${Math.min(tmr, 100)}%`, width: 2, background: T.navy, zIndex: 2 }}>
                                        <div style={{ position: "absolute", top: 0, left: -22, fontSize: 9, color: T.navy, fontWeight: 700, whiteSpace: "nowrap" }}>TMR {tmr}%</div>
                                    </div>
                                </div>
                                <div style={{ display: "flex", gap: 16, marginTop: 6, fontSize: 11.5, color: T.sub }}>
                                    <span>TE : <strong style={{ color: T.red }}>{r.te_pct}%</strong></span>
                                    <span>TC : <strong style={{ color: T.amber }}>{r.tc_pct}%</strong></span>
                                    <span>{r.total_mesures} mesures</span>
                                </div>
                            </div>
                        );
                    })}
                    <div style={{ fontSize: 11.5, color: T.faint, fontStyle: "italic", marginTop: 4 }}>La ligne verticale représente le TMR ({tmr}%) — référence comparative.</div>
                </div>
            )}

            {tab === "bareme" && (
                <div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                        {BAREME.map((b) => (
                            <div key={b.signe} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", borderRadius: 10, background: `${b.color}0F`, border: `1.5px solid ${b.color}33` }}>
                                <span style={{ fontFamily: T.mono, fontSize: 17, fontWeight: 700, color: b.color, minWidth: 40 }}>{b.signe}</span>
                                <span style={{ fontSize: 13, fontWeight: 700, color: T.ink, minWidth: 180 }}>{b.label}</span>
                                <span style={{ fontSize: 12, color: T.sub }}>{b.regle}</span>
                            </div>
                        ))}
                    </div>
                    <div style={{ padding: "14px 18px", background: T.cardAlt, borderRadius: 10, fontSize: 12.5, color: T.sub, lineHeight: 1.8, border: `1px solid ${T.line}` }}>
                        <strong style={{ color: T.ink }}>Formule :</strong> ratio = TR% de l'opérateur ÷ TMR global<br />
                        <strong style={{ color: T.ink }}>TMR actuel :</strong> {tmr}% (moyenne des TR% de tous les opérateurs)<br />
                        <strong style={{ color: T.ink }}>Utilisation :</strong> compare objectivement chaque opérateur à la moyenne nationale.
                    </div>
                </div>
            )}
        </Card>
    );
}

/* ─────────────────────────────────────────────────────────
   Shared chart styling
   ───────────────────────────────────────────────────────── */
const axisProps = { tick: { fontSize: 12, fill: CHART_AXIS, fontFamily: T.mono }, stroke: CHART_AXIS, tickLine: false };
const tooltipStyle = {
    contentStyle: { borderRadius: 10, border: `1px solid ${T.line}`, background: T.card, boxShadow: "0 6px 20px rgba(16,24,40,.12)", fontFamily: T.sans, fontSize: 12.5 },
    labelStyle: { color: T.ink, fontWeight: 700 },
    itemStyle: { color: T.sub },
};

function ChartCard({ title, children, right }) {
    return (
        <div>
            <SectionTitle right={right}>{title}</SectionTitle>
            <Card style={{ padding: "16px 18px 12px" }}>{children}</Card>
        </div>
    );
}

function LegendChips({ items }) {
    return (
        <div style={{ display: "flex", gap: 18, justifyContent: "center", marginTop: 8, flexWrap: "wrap" }}>
            {items.map((it) => (
                <span key={it.label} style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 600, color: T.sub }}>
                    <span style={{ width: 9, height: 9, borderRadius: 3, background: it.color, flexShrink: 0 }} />{it.label}
                </span>
            ))}
        </div>
    );
}

/* ─────────────────────────────────────────────────────────
   2. Graphiques
   ───────────────────────────────────────────────────────── */
function SectionGraphiques({ token }) {
    const [kpiData, setKpiData] = useState([]);
    const [qaData, setQaData] = useState([]);
    const [evolData, setEvolData] = useState([]);
    const [villeData, setVilleData] = useState([]);
    const [dureeData, setDureeData] = useState([]);
    const [sigData, setSigData] = useState([]);
    const [mode, setMode] = useState("campagne");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            apiFetch("/api/kpi/reussite", token),
            apiFetch("/api/kpi/qualite_par_ville", token),
            apiFetch("/api/campagnes", token),
            apiFetch("/api/kpi/par_ville", token).catch(() => ({})),
            apiFetch("/api/ml/anomalies", token).catch(() => ({})),
        ]).then(([kpi, qa, camp, parVille, anom]) => {
            setKpiData((kpi.results || []).map((r) => ({ operateur: r.operateur, TR: r.tr_pct, TC: r.tc_pct, TE: r.te_pct })));
            const opList = qa.operateurs || [];
            const ens = qa.table?.["Ensemble des sites"] || {};
            if (opList.length > 0 && ens[opList[0]]) {
                const d = ens["Ensemble"] || ens[opList[0]];
                setQaData([
                    { name: "Parfaite", value: d.parfaite, color: T.green },
                    { name: "Acceptable", value: d.acceptable, color: T.blue },
                    { name: "Médiocre", value: d.mediocre, color: T.amber },
                    { name: "Mauvaise", value: d.mauvaise, color: T.red },
                ].filter((x) => x.value > 0));
            }
            setEvolData((camp.campagnes || []).map((c, i) => ({ campagne: `C${i + 1}`, date: c.date_mesure, mesures: c.nb_mesures })));

            // TR% moyen par ville (top 8)
            const villes = {};
            (parVille.results || []).forEach((r) => {
                (villes[r.site_emission] = villes[r.site_emission] || []).push(r.tr_pct || 0);
            });
            setVilleData(Object.entries(villes)
                .map(([v, vals]) => ({ ville: v, tr_moy: Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) }))
                .sort((a, b) => b.tr_moy - a.tr_moy).slice(0, 8));

            // Histogramme des durées
            const buckets = { "0-5s": 0, "5-30s": 0, "30-60s": 0, "60-90s": 0, "90-120s": 0, ">120s": 0 };
            (anom.results || []).forEach((r) => {
                const d = r.duree || 0;
                if (d <= 5) buckets["0-5s"]++; else if (d <= 30) buckets["5-30s"]++;
                else if (d <= 60) buckets["30-60s"]++; else if (d <= 90) buckets["60-90s"]++;
                else if (d <= 120) buckets["90-120s"]++; else buckets[">120s"]++;
            });
            setDureeData(Object.entries(buckets).map(([range, count]) => ({ range, count })));

            // Signal moyen par opérateur
            const sigOp = {};
            (anom.results || []).forEach((r) => {
                (sigOp[r.operateur] = sigOp[r.operateur] || []).push(r.signal_strength || 0);
            });
            setSigData(Object.entries(sigOp).map(([operateur, vals]) => ({
                operateur, signal_moy: Math.round(vals.reduce((a, b) => a + b, 0) / vals.length),
            })));
        }).catch(() => { }).finally(() => setLoading(false));
    }, [token]);

    if (loading) return <Loading label="Chargement des graphiques…" />;

    // Cumulé transforme réellement la donnée (somme cumulative)
    let running = 0;
    const chartData = mode === "cumule"
        ? evolData.map((d) => ({ ...d, mesures: (running += d.mesures) }))
        : evolData;

    const totalMesures = evolData.reduce((a, b) => a + b.mesures, 0);
    const qBon = qaData.filter((q) => q.name === "Parfaite" || q.name === "Acceptable").reduce((a, b) => a + b.value, 0);

    const seg = (id, lbl) => (
        <button onClick={() => setMode(id)} style={{
            padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer", fontFamily: T.sans,
            fontSize: 12.5, fontWeight: 700, background: mode === id ? T.card : "transparent",
            color: mode === id ? T.ink : T.sub, boxShadow: mode === id ? "0 1px 2px rgba(0,0,0,.3)" : "none", transition: "all .15s",
        }}>{lbl}</button>
    );

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Grand graphe en aires + donut PESQ — layout référence image 1 */}
            <div style={{ display: "grid", gridTemplateColumns: "1.55fr 1fr", gap: 14 }}>
                <Card style={{ padding: "18px 20px" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
                        <div>
                            <div style={{ fontSize: 15, fontWeight: 800, color: T.ink, letterSpacing: "-.01em" }}>Évolution des mesures</div>
                            <div style={{ fontSize: 12.5, color: T.sub, marginTop: 2 }}>Volume collecté au fil des campagnes.</div>
                        </div>
                        <div style={{ display: "flex", gap: 3, padding: 3, borderRadius: 9, background: T.lineSoft, flexShrink: 0 }}>
                            {seg("campagne", "Par campagne")}{seg("cumule", "Cumulé")}
                        </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: 6, fontSize: 12, fontWeight: 600, color: T.sub }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: T.blue }} />Mesures</span>
                        <span style={{ marginLeft: "auto", fontFamily: T.mono, color: T.faint }}>Total : <strong style={{ color: T.ink }}>{totalMesures}</strong></span>
                    </div>
                    <ResponsiveContainer width="100%" height={188}>
                        <AreaChart data={chartData} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
                            <defs>
                                <linearGradient id="gArea" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={T.blue} stopOpacity={.26} />
                                    <stop offset="100%" stopColor={T.blue} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} vertical={false} />
                            <XAxis dataKey="campagne" {...axisProps} />
                            <YAxis {...axisProps} width={34} />
                            <Tooltip {...tooltipStyle} formatter={(v) => [v, mode === "cumule" ? "Cumul" : "Mesures"]}
                                labelFormatter={(l) => { const d = chartData.find((x) => x.campagne === l); return d ? `${l} — ${d.date}` : l; }} />
                            <Area isAnimationActive={false} type="monotone" dataKey="mesures" stroke={T.blue} strokeWidth={2.25} fill="url(#gArea)" dot={{ fill: T.blue, r: 2.5, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                        </AreaChart>
                    </ResponsiveContainer>
                </Card>

                {qaData.length > 0 && (
                    <Card style={{ padding: "18px 20px", display: "flex", flexDirection: "column" }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: T.ink, letterSpacing: "-.01em" }}>Qualité auditive</div>
                        <div style={{ fontSize: 12.5, color: T.sub, marginTop: 2 }}>Distribution PESQ (MOS-LQO).</div>
                        <div style={{ position: "relative", margin: "2px 0 0" }}>
                            <ResponsiveContainer width="100%" height={148}>
                                <PieChart>
                                    <Pie data={qaData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={46} outerRadius={68} paddingAngle={2} stroke={T.card} strokeWidth={2}>
                                        {qaData.map((e, i) => <Cell key={i} fill={e.color} />)}
                                    </Pie>
                                    <Tooltip {...tooltipStyle} formatter={(v) => `${v.toFixed(1)}%`} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                                <div style={{ fontSize: 22, fontWeight: 800, color: T.ink, letterSpacing: "-.02em" }}>{qBon.toFixed(0)}%</div>
                                <div style={{ fontSize: 10.5, color: T.faint, fontWeight: 600 }}>acceptable+</div>
                            </div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 1, marginTop: 4 }}>
                            {qaData.map((q) => (
                                <div key={q.name} style={{ display: "flex", alignItems: "center", gap: 9, padding: "6px 0", borderTop: `1px solid ${T.lineSoft}` }}>
                                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: q.color, flexShrink: 0 }} />
                                    <span style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>{q.name}</span>
                                    <span style={{ marginLeft: "auto", fontSize: 12.5, fontWeight: 800, fontFamily: T.mono, color: T.sub }}>{q.value.toFixed(1)}%</span>
                                </div>
                            ))}
                        </div>
                    </Card>
                )}
            </div>

            {/* TE/TC/TR + TR% par ville — sur la même ligne, design pro */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, alignItems: "start" }}>
                <ChartCard title="Répartition TE / TC / TR par opérateur">
                    <ResponsiveContainer width="100%" height={230}>
                        <BarChart data={kpiData} margin={{ top: 6, right: 8, bottom: 0, left: 0 }} barCategoryGap="34%" barGap={4}>
                            <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} vertical={false} />
                            <XAxis dataKey="operateur" {...axisProps} tick={{ ...axisProps.tick, fontFamily: T.sans, fontWeight: 600 }} />
                            <YAxis {...axisProps} unit="%" width={40} domain={[0, 100]} tickCount={6} />
                            <Tooltip {...tooltipStyle} formatter={(v) => `${v}%`} cursor={{ fill: `${T.navy}06` }} />
                            <Bar isAnimationActive={false} dataKey="TR" name="TR (Réussite)" fill={T.blue} radius={[4, 4, 0, 0]} maxBarSize={20} />
                            <Bar isAnimationActive={false} dataKey="TC" name="TC (Coupure)" fill={T.purple} radius={[4, 4, 0, 0]} maxBarSize={20} />
                            <Bar isAnimationActive={false} dataKey="TE" name="TE (Échec)" fill={T.amber} radius={[4, 4, 0, 0]} maxBarSize={20} />
                        </BarChart>
                    </ResponsiveContainer>
                    <LegendChips items={[{ label: "TR (Réussite)", color: T.blue }, { label: "TC (Coupure)", color: T.purple }, { label: "TE (Échec)", color: T.amber }]} />
                </ChartCard>

                {villeData.length > 0 && (
                    <ChartCard title="Taux de réussite (TR%) par ville">
                        <ResponsiveContainer width="100%" height={230}>
                            <BarChart data={villeData} layout="vertical" margin={{ top: 4, right: 24, bottom: 0, left: 4 }} barCategoryGap="26%">
                                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} horizontal={false} />
                                <XAxis type="number" {...axisProps} unit="%" domain={[0, 100]} tickCount={5} />
                                <YAxis type="category" dataKey="ville" {...axisProps} tick={{ ...axisProps.tick, fontFamily: T.sans, fontWeight: 600 }} width={84} />
                                <Tooltip {...tooltipStyle} formatter={(v) => `${v}%`} cursor={{ fill: `${T.navy}06` }} />
                                <Bar isAnimationActive={false} dataKey="tr_moy" name="TR% moyen" radius={[0, 4, 4, 0]} maxBarSize={16}>
                                    {villeData.map((v, i) => <Cell key={i} fill={v.tr_moy >= 90 ? "#0B3B8C" : v.tr_moy >= 80 ? "#1D5FC2" : v.tr_moy >= 70 ? "#3E7FE0" : v.tr_moy >= 60 ? "#7FAEF0" : "#C9DEFB"} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                )}
            </div>

            {/* Durées + Signal côte à côte */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                {dureeData.some((d) => d.count > 0) && (
                    <ChartCard title="Distribution des durées d'appel">
                        <ResponsiveContainer width="100%" height={168}>
                            <BarChart data={dureeData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} vertical={false} />
                                <XAxis dataKey="range" {...axisProps} tick={{ ...axisProps.tick, fontSize: 10.5 }} />
                                <YAxis {...axisProps} width={30} />
                                <Tooltip {...tooltipStyle} formatter={(v) => [v, "Mesures"]} cursor={{ fill: `${T.navy}08` }} />
                                <Bar isAnimationActive={false} dataKey="count" name="Nb mesures" radius={[4, 4, 0, 0]} maxBarSize={26}>
                                    {dureeData.map((d, i) => <Cell key={i} fill={d.range === "0-5s" ? T.red : d.range === ">120s" ? T.green : T.amber} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                )}

                {sigData.length > 0 && (
                    <ChartCard title="Signal moyen par opérateur (dBm)">
                        <ResponsiveContainer width="100%" height={168}>
                            <BarChart data={sigData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} vertical={false} />
                                <XAxis dataKey="operateur" {...axisProps} tick={{ ...axisProps.tick, fontFamily: T.sans, fontWeight: 600 }} />
                                <YAxis {...axisProps} unit=" dBm" domain={[-120, -40]} width={58} />
                                <Tooltip {...tooltipStyle} formatter={(v) => `${v} dBm`} cursor={{ fill: `${T.navy}08` }} />
                                <Bar isAnimationActive={false} dataKey="signal_moy" name="Signal moyen" radius={[4, 4, 0, 0]} maxBarSize={30}>
                                    {sigData.map((d, i) => <Cell key={i} fill={d.signal_moy > -75 ? T.green : d.signal_moy > -85 ? T.amber : T.red} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                        <div style={{ fontSize: 10.5, color: T.faint, marginTop: 6, textAlign: "center" }}>
                            vert &gt; −75 (bon) · orange −75 à −85 · rouge &lt; −85 dBm
                        </div>
                    </ChartCard>
                )}
            </div>
        </div>
    );
}

/* ─────────────────────────────────────────────────────────
   3. Mesures
   ───────────────────────────────────────────────────────── */
function SectionMesures({ token }) {
    const [data, setData] = useState(null);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [confirm, setConfirm] = useState(null);
    const [search, setSearch] = useState("");
    const [msg, setMsg] = useState(null);
    const [hover, setHover] = useState(null);

    const charger = useCallback((p = 1) => {
        setLoading(true);
        apiFetch(`/api/admin/mesures?page=${p}&per_page=15`, token)
            .then((d) => { setData(d); setPage(p); }).catch(() => { }).finally(() => setLoading(false));
    }, [token]);
    useEffect(() => { charger(1); }, [charger]);

    async function supprimer(id) {
        if (confirm !== id) { setConfirm(id); return; }
        try {
            await apiFetch(`/api/admin/mesures/${id}`, token, { method: "DELETE" });
            setMsg({ type: "success", text: `Mesure id=${id} supprimée avec succès.` });
            setConfirm(null); charger(page);
        } catch { setMsg({ type: "error", text: "Erreur lors de la suppression." }); }
    }

    function classe(d) {
        if (d <= 5) return { label: "TE", color: T.red };
        if (d < 120) return { label: "TC", color: T.amber };
        return { label: "TR", color: T.green };
    }

    const mesures = (data?.mesures || []).filter((m) =>
        !search || String(m.mesure).includes(search) ||
        (m.operateur || "").toLowerCase().includes(search.toLowerCase()) ||
        (m.site_emission || "").toLowerCase().includes(search.toLowerCase()));

    const th = { padding: "11px 12px", color: "#C6D4E8", fontWeight: 700, textAlign: "left", fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".05em", whiteSpace: "nowrap" };
    const td = { padding: "12px 12px", fontSize: 12.5, color: T.ink, borderBottom: `1px solid ${T.lineSoft}` };

    return (
        <div>
            {msg && <div style={{ marginBottom: 14 }}><Alert type={msg.type}>{msg.text}</Alert></div>}

            <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center" }}>
                <Field icon={Ico.search} flex={1} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher par n°, opérateur ou ville…" />
                <Btn small variant="outline" color={T.navy} onClick={() => charger(page)} loading={loading}><Icon d={Ico.refresh} size={14} />Actualiser</Btn>
                <span style={{ fontSize: 12.5, color: T.sub, whiteSpace: "nowrap" }}>
                    <b style={{ color: T.ink, fontFamily: T.mono }}>{data?.total || 0}</b> mesures · p.{data?.page || 1}/{data?.pages || 1}
                </span>
            </div>

            <div style={{ border: `1px solid ${T.line}`, borderRadius: 12, overflow: "hidden", background: T.card }}>
                <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.sans }}>
                        <thead>
                            <tr style={{ background: T.navy }}>
                                {["ID", "N°", "Opérateur", "Ville", "Durée", "Classe", "PESQ", "Signal", "Date", ""].map((h, i) => (
                                    <th key={i} style={{ ...th, textAlign: i === 9 ? "right" : "left" }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {mesures.map((m) => {
                                const cl = classe(m.duree || 0); const hov = hover === m.id;
                                return (
                                    <tr key={m.id} onMouseEnter={() => setHover(m.id)} onMouseLeave={() => setHover(null)}
                                        style={{ background: hov ? T.cardAlt : "transparent", transition: "background .12s" }}>
                                        <td style={{ ...td, color: T.faint, fontFamily: T.mono }}>{m.id}</td>
                                        <td style={{ ...td, fontFamily: T.mono, fontWeight: 700 }}>{m.mesure}</td>
                                        <td style={td}>
                                            <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                                                <span style={{ width: 8, height: 8, borderRadius: "50%", background: opColor(m.operateur), flexShrink: 0 }} />
                                                <span style={{ fontWeight: 600 }}>{m.operateur}</span>
                                            </span>
                                        </td>
                                        <td style={td}>{m.site_emission}</td>
                                        <td style={{ ...td, fontFamily: T.mono }}>{m.duree}s</td>
                                        <td style={td}><Badge color={cl.color}>{cl.label}</Badge></td>
                                        <td style={{ ...td, fontSize: 11.5, color: T.sub, fontFamily: T.mono }}>{m.quality_pesq ? m.quality_pesq.substring(0, 12) + "…" : "—"}</td>
                                        <td style={{ ...td, fontFamily: T.mono, color: T.sub }}>{m.signal_strength} dBm</td>
                                        <td style={{ ...td, fontSize: 11.5, color: T.sub, whiteSpace: "nowrap" }}>{m.date_mesure}</td>
                                        <td style={{ ...td, textAlign: "right" }}>
                                            <Btn small variant={confirm === m.id ? "solid" : "ghost"} color={confirm === m.id ? T.red : T.faint} onClick={() => supprimer(m.id)}>
                                                {confirm === m.id ? "Confirmer ?" : "Supprimer"}
                                            </Btn>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {data && data.pages > 1 && (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, marginTop: 18, flexWrap: "wrap" }}>
                    <Btn small variant="outline" color={T.navy} disabled={page <= 1} onClick={() => charger(page - 1)}><Icon d={Ico.chevL} size={14} />Préc.</Btn>
                    {Array.from({ length: Math.min(data.pages, 10) }, (_, i) => i + 1).map((p) => (
                        <button key={p} onClick={() => charger(p)} style={{
                            minWidth: 34, height: 34, borderRadius: 9, cursor: "pointer", fontFamily: T.mono, fontSize: 12.5, fontWeight: 700,
                            border: `1px solid ${p === page ? T.navy : T.line}`, background: p === page ? T.navy : T.card, color: p === page ? "#fff" : T.sub,
                        }}>{p}</button>
                    ))}
                    <Btn small variant="outline" color={T.navy} disabled={page >= data.pages} onClick={() => charger(page + 1)}>Suiv.<Icon d={Ico.chevR} size={14} /></Btn>
                </div>
            )}
        </div>
    );
}

/* ─────────────────────────────────────────────────────────
   4. Export
   ───────────────────────────────────────────────────────── */
function SectionExport({ token }) {
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState(null);
    const [campagnes, setCampagnes] = useState([]);
    const [filtreCamp, setFiltreCamp] = useState("tous");
    const [filtreOp, setFiltreOp] = useState("tous");
    const [operateurs, setOperateurs] = useState([]);

    useEffect(() => {
        apiFetch("/api/campagnes", token).then((d) => setCampagnes(d.campagnes || [])).catch(() => { });
        apiFetch("/api/operateurs", token).then((d) => setOperateurs(d.operateurs || [])).catch(() => { });
    }, [token]);

    async function getMesuresFiltrees() {
        const data = await apiFetch("/api/admin/mesures?page=1&per_page=9999", token);
        let mesures = data.mesures || [];
        if (filtreCamp !== "tous") mesures = mesures.filter((m) => m.date_mesure === filtreCamp);
        if (filtreOp !== "tous") mesures = mesures.filter((m) => m.operateur === filtreOp);
        return mesures;
    }

    async function exporterCSV() {
        setLoading(true); setMsg(null);
        try {
            const mesures = await getMesuresFiltrees();
            if (!mesures.length) { setMsg({ type: "warning", text: "Aucune mesure pour ces filtres." }); return; }
            const cols = ["id", "mesure", "service", "operateur", "site_emission", "duree", "quality_pesq", "signal_strength", "date_mesure", "heure_gps", "latitude", "longitude", "created_at"];
            const csv = [cols.join(";")].concat(mesures.map((m) => cols.map((c) => `"${m[c] ?? ""}"`).join(";"))).join("\n");
            const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob); const a = document.createElement("a");
            const suffix = filtreCamp !== "tous" ? `_${filtreCamp}` : "";
            a.href = url; a.download = `qos_mesures${suffix}_${new Date().toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(url);
            setMsg({ type: "success", text: `${mesures.length} mesure(s) exportée(s).` });
        } catch { setMsg({ type: "error", text: "Erreur lors de l'export." }); }
        finally { setLoading(false); }
    }

    async function exporterRapport() {
        setLoading(true); setMsg(null);
        try {
            const mesures = await getMesuresFiltrees();
            const [stats, kpi] = await Promise.all([apiFetch("/api/admin/stats", token), apiFetch("/api/kpi/reussite", token)]);
            const titre = filtreCamp !== "tous" ? `Campagne du ${filtreCamp}` : "Toutes les campagnes";
            const lignes = [
                "RAPPORT DE SYNTHÈSE — PLATEFORME QoS 4G/5G",
                `Généré le : ${new Date().toLocaleString("fr-FR")}`,
                `Filtre    : ${titre}${filtreOp !== "tous" ? ` · Opérateur : ${filtreOp}` : ""}`, "",
                "=== STATISTIQUES GÉNÉRALES ===",
                `Total mesures     : ${stats.total_mesures}`,
                `Opérateurs        : ${stats.nb_operateurs}`,
                `Villes couvertes  : ${stats.nb_villes}`,
                `Campagnes         : ${stats.nb_campagnes}`,
                `Première campagne : ${stats.premiere_campagne || "—"}`,
                `Dernière campagne : ${stats.derniere_campagne || "—"}`, "",
                "=== TAUX DE RÉUSSITE PAR OPÉRATEUR ===",
                ["Opérateur", "Mesures", "TE%", "TC%", "TR%", "TMR%", "Signe"].join("\t"),
                ...(kpi.results || []).map((r) => [r.operateur, r.total_mesures, r.te_pct, r.tc_pct, r.tr_pct, r.tmr, r.signe].join("\t")),
                "",
                `=== MESURES EXPORTÉES (${mesures.length}) ===`,
                ["N°", "Opérateur", "Ville", "Durée", "Signal", "PESQ", "Date"].join("\t"),
                ...mesures.map((m) => [m.mesure, m.operateur, m.site_emission, m.duree, m.signal_strength, m.quality_pesq, m.date_mesure].join("\t")),
            ];
            const blob = new Blob([lignes.join("\n")], { type: "text/plain;charset=utf-8;" });
            const url = URL.createObjectURL(blob); const a = document.createElement("a");
            a.href = url; a.download = `qos_rapport_${new Date().toISOString().slice(0, 10)}.txt`; a.click(); URL.revokeObjectURL(url);
            setMsg({ type: "success", text: "Rapport exporté avec succès." });
        } catch { setMsg({ type: "error", text: "Erreur lors de l'export du rapport." }); }
        finally { setLoading(false); }
    }

    const selectStyle = {
        padding: "10px 12px", borderRadius: 9, border: `1.5px solid ${T.line}`, fontSize: 13,
        background: T.card, outline: "none", fontFamily: T.sans, color: T.ink, width: "100%",
    };

    const cards = [
        { icon: Ico.download, color: T.blue, title: "Export CSV — Mesures", desc: "Télécharge les mesures sélectionnées au format CSV, compatible Excel (encodage UTF-8).", btn: "Télécharger CSV", fn: exporterCSV },
        { icon: Ico.down, color: T.green, title: "Export Rapport — Synthèse", desc: "Génère un rapport texte avec statistiques globales, KPI par opérateur et liste des mesures sélectionnées.", btn: "Télécharger Rapport", fn: exporterRapport },
    ];

    const filtresActifs = filtreCamp !== "tous" || filtreOp !== "tous";

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {msg && <Alert type={msg.type}>{msg.text}</Alert>}

            <Card style={{ padding: "18px 22px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                    <span style={{ width: 3, height: 15, background: T.blue, borderRadius: 2 }} />
                    <span style={{ fontSize: 13, fontWeight: 800, color: T.ink }}>Choisissez les données à exporter</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: T.faint, textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 6 }}>Campagne</label>
                        <select value={filtreCamp} onChange={(e) => setFiltreCamp(e.target.value)} style={selectStyle}>
                            <option value="tous">Toutes les campagnes</option>
                            {campagnes.map((c) => <option key={c.date_mesure} value={c.date_mesure}>{c.date_mesure} ({c.nb_mesures} mesures)</option>)}
                        </select>
                    </div>
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: T.faint, textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 6 }}>Opérateur</label>
                        <select value={filtreOp} onChange={(e) => setFiltreOp(e.target.value)} style={selectStyle}>
                            <option value="tous">Tous les opérateurs</option>
                            {operateurs.map((op) => <option key={op} value={op}>{op}</option>)}
                        </select>
                    </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, fontSize: 12.5, color: T.sub, fontWeight: 600 }}>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: filtresActifs ? T.blue : T.faint, flexShrink: 0 }} />
                    {filtresActifs
                        ? `Export filtré — campagne ${filtreCamp !== "tous" ? filtreCamp : "toutes"} · opérateur ${filtreOp !== "tous" ? filtreOp : "tous"}`
                        : "Export complet — toutes les mesures"}
                </div>
            </Card>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                {cards.map((c) => (
                    <Card key={c.title} style={{ padding: 22, display: "flex", flexDirection: "column", gap: 12 }}>
                        <div style={{ width: 44, height: 44, borderRadius: 12, background: `${c.color}12`, color: c.color, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Icon d={c.icon} size={21} color={c.color} />
                        </div>
                        <div style={{ fontSize: 15.5, fontWeight: 800, color: T.ink }}>{c.title}</div>
                        <div style={{ fontSize: 13, color: T.sub, lineHeight: 1.6, flex: 1 }}>{c.desc}</div>
                        <div><Btn onClick={c.fn} loading={loading} color={c.color}><Icon d={Ico.download} size={16} color="#fff" />{c.btn}</Btn></div>
                    </Card>
                ))}
            </div>
        </div>
    );
}


/* ─────────────────────────────────────────────────────────
   5. Alertes
   ───────────────────────────────────────────────────────── */
function SectionAlertes({ token }) {
    const [alertes, setAlertes] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            apiFetch("/api/kpi/reussite", token),
            apiFetch("/api/ml/anomalies", token),
            apiFetch("/api/campagnes", token),
            apiFetch("/api/kpi/qualite_par_ville", token).catch(() => ({})),
            apiFetch("/api/admin/etl-log", token).catch(() => null),
            apiFetch("/api/admin/mesures?page=1&per_page=9999", token).catch(() => ({})),
        ]).then(([kpi, anom, camp, qa, etlLog, mesures]) => {
            const al = [];
            const rows = kpi.results || [];
            const allM = mesures.mesures || [];

            // 1. TR% < 50%
            rows.forEach((r) => { if (r.tr_pct < 50) al.push({ type: "error", icon: Ico.chart, titre: `Taux de réussite faible — ${r.operateur}`, detail: `TR% = ${r.tr_pct}% (seuil 50%). Vérifiez la couverture réseau de cet opérateur.` }); });

            // 2. TE% > 10%
            rows.forEach((r) => { if (r.te_pct > 10) al.push({ type: "error", icon: Ico.warn, titre: `Trop d'échecs d'appel — ${r.operateur}`, detail: `TE% = ${r.te_pct}% (seuil 10%)${r.nb_TE != null ? ` · ${r.nb_TE} appels échoués sur ${r.total_mesures}` : ""}.` }); });

            // 3. Anomalies ML > 30%
            const total = anom.total_mesures || 0, nbAnom = anom.nb_anomalies || 0;
            if (total > 0 && nbAnom / total > 0.3) al.push({ type: "warning", icon: Ico.warn, titre: "Taux d'anomalies ML élevé", detail: `${nbAnom} anomalies sur ${total} mesures (${Math.round(nbAnom / total * 100)}% > 30%).` });

            // 4. Aucune collecte > 7 jours
            const campagnes = camp.campagnes || [];
            if (campagnes.length > 0) {
                const derniere = campagnes[campagnes.length - 1].date_mesure;
                const parts = derniere.split("-");
                const diff = (new Date() - new Date(parts[2], parts[1] - 1, parts[0])) / 86400000;
                if (diff > 7) al.push({ type: "warning", icon: Ico.clock, titre: "Aucune collecte récente", detail: `Dernière campagne il y a ${Math.round(diff)} jours (${derniere}). Pensez à relancer une campagne terrain.` });
            }

            // 5. Signal moyen < -85 dBm
            rows.forEach((r) => {
                const l = allM.filter((m) => m.operateur === r.operateur);
                if (l.length > 0) {
                    const moy = l.reduce((s, m) => s + (parseFloat(m.signal_strength) || 0), 0) / l.length;
                    if (moy < -85) al.push({ type: "warning", icon: Ico.signal, titre: `Signal faible — ${r.operateur}`, detail: `Signal moyen = ${Math.round(moy)} dBm (seuil −85 dBm). Couverture réseau insuffisante.` });
                }
            });

            // 6. Qualité vocale dégradée > 40%
            const ops = qa.operateurs || [], ens = qa.table?.["Ensemble des sites"] || {};
            ops.forEach((op) => { const d = ens[op]; if (d && (d.mauvaise + d.mediocre) > 40) al.push({ type: "warning", icon: Ico.chart, titre: `Qualité vocale dégradée — ${op}`, detail: `${Math.round(d.mauvaise + d.mediocre)}% de mesures Mauvaise ou Médiocre (seuil 40%).` }); });

            // 7. ETL non exécuté / erreur
            if (!etlLog || !etlLog.contenu) {
                al.push({ type: "warning", icon: Ico.etl, titre: "Pipeline ETL jamais exécuté", detail: "Aucun fichier etl.log trouvé. Le Cron Job n'a pas encore tourné ou est mal configuré." });
            } else {
                const last5 = etlLog.contenu.split("\n").slice(-5).join(" ");
                if (/ERREUR|Error/.test(last5)) al.push({ type: "error", icon: Ico.etl, titre: "Erreur lors du dernier run ETL", detail: "Le pipeline ETL a rencontré une erreur lors de sa dernière exécution. Consultez les logs." });
            }

            // 8. Mesures sans GPS
            const sansGps = allM.filter((m) => !m.latitude || m.latitude === "" || m.latitude === "0");
            if (sansGps.length > 0) al.push({ type: "warning", icon: Ico.map, titre: `${sansGps.length} mesure(s) sans coordonnées GPS`, detail: "Ces mesures n'apparaîtront pas sur la carte. Activez le GPS avant la prochaine campagne." });

            // 9. Déséquilibre entre opérateurs
            const totaux = rows.map((r) => r.total_mesures || 0);
            if (totaux.length >= 2) {
                const max = Math.max(...totaux), min = Math.min(...totaux);
                if (min > 0 && max / min > 3) {
                    const opMax = rows.find((r) => r.total_mesures === max)?.operateur;
                    const opMin = rows.find((r) => r.total_mesures === min)?.operateur;
                    al.push({ type: "warning", icon: Ico.layers, titre: "Déséquilibre entre opérateurs", detail: `${opMax} a ${max} mesures contre ${min} pour ${opMin} (ratio > 3). La campagne n'est pas équilibrée.` });
                }
            }

            if (al.length === 0) al.push({ type: "success", icon: Ico.check, titre: "Aucune alerte active", detail: "Tous les indicateurs sont dans les limites normales. La plateforme fonctionne correctement." });
            setAlertes(al);
        }).catch(() => { }).finally(() => setLoading(false));
    }, [token]);

    if (loading) return <Loading label="Analyse en cours…" />;

    const colors = { success: T.green, warning: T.amber, error: T.red };
    const labels = { success: "OK", warning: "Attention", error: "Critique" };
    const nbErr = alertes.filter((a) => a.type === "error").length;
    const nbWarn = alertes.filter((a) => a.type === "warning").length;

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {alertes[0]?.type !== "success" && (nbErr > 0 || nbWarn > 0) && (
                <div style={{ display: "flex", gap: 9, marginBottom: 2 }}>
                    {nbErr > 0 && <Badge color={T.red}>{nbErr} critique{nbErr > 1 ? "s" : ""}</Badge>}
                    {nbWarn > 0 && <Badge color={T.amber}>{nbWarn} attention{nbWarn > 1 ? "s" : ""}</Badge>}
                </div>
            )}
            {alertes.map((a, i) => (
                <Card key={i} style={{ padding: "16px 20px", borderLeft: `4px solid ${colors[a.type]}` }}>
                    <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                        <div style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0, background: `${colors[a.type]}14`, color: colors[a.type], display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Icon d={a.icon} size={19} color={colors[a.type]} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 800, color: T.ink, marginBottom: 4 }}>{a.titre}</div>
                            <div style={{ fontSize: 13, color: T.sub, lineHeight: 1.6 }}>{a.detail}</div>
                        </div>
                        <Badge color={colors[a.type]}>{labels[a.type]}</Badge>
                    </div>
                </Card>
            ))}
        </div>
    );
}

/* ─────────────────────────────────────────────────────────
   6. Campagnes
   ───────────────────────────────────────────────────────── */
function SectionCampagnes({ token }) {
    const [campagnes, setCampagnes] = useState([]);
    const [compare, setCompare] = useState([]);
    const [kpiComp, setKpiComp] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        apiFetch("/api/campagnes", token).then((d) => setCampagnes((d.campagnes || []).reverse()))
            .catch(() => { }).finally(() => setLoading(false));
    }, [token]);

    async function toggleCompare(date) {
        const next = compare.includes(date) ? compare.filter((d) => d !== date) : [...compare.slice(-1), date];
        setCompare(next);
        if (!kpiComp[date]) {
            const parts = date.split("-"); const iso = `${parts[2]}-${parts[1]}-${parts[0]}`;
            const res = await apiFetch(`/api/kpi/filtre?date_debut=${iso}&date_fin=${iso}`, token);
            setKpiComp((prev) => ({ ...prev, [date]: res.results || [] }));
        }
    }

    if (loading) return <Loading />;

    const stat = (v, c, lbl) => (
        <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 19, fontWeight: 800, fontFamily: T.mono, color: c }}>{v}</div>
            <div style={{ fontSize: 10, color: T.faint, marginTop: 2 }}>{lbl}</div>
        </div>
    );

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
                {campagnes.map((c, i) => {
                    const on = compare.includes(c.date_mesure);
                    return (
                        <Card key={c.date_mesure} style={{
                            padding: "16px 18px", cursor: "pointer", transition: "border-color .2s,box-shadow .2s",
                            border: `1px solid ${on ? T.accent : T.line}`, boxShadow: on ? `0 0 0 3px ${T.accent}22` : "0 1px 2px rgba(0,0,0,.25)",
                        }} onClick={() => toggleCompare(c.date_mesure)}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                <div>
                                    <div style={{ fontSize: 13.5, fontWeight: 800, color: T.ink }}>Campagne {campagnes.length - i}</div>
                                    <div style={{ fontSize: 12, color: T.sub, marginTop: 2, fontFamily: T.mono }}>{c.date_mesure}</div>
                                </div>
                                {on ? <Badge color={T.red}>Sélectionnée</Badge>
                                    : <span style={{ width: 18, height: 18, borderRadius: 6, border: `1.5px solid ${T.line}` }} />}
                            </div>
                            <div style={{ display: "flex", gap: 16, marginTop: 14 }}>
                                {stat(c.nb_mesures, T.accent, "mesures")}
                                {stat(c.nb_operateurs, T.blue, "opérateurs")}
                                {stat(c.nb_villes, T.green, "villes")}
                            </div>
                        </Card>
                    );
                })}
            </div>

            {compare.length === 2 && kpiComp[compare[0]] && kpiComp[compare[1]] && (
                <Card style={{ padding: 20 }}>
                    <SectionTitle>Comparaison des campagnes</SectionTitle>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                        {compare.map((date) => (
                            <div key={date}>
                                <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 800, color: T.ink, marginBottom: 10 }}>
                                    <Icon d={Ico.flag} size={15} color={T.red} />{date}
                                </div>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: T.sans }}>
                                    <thead>
                                        <tr style={{ background: T.lineSoft }}>
                                            {["Opérateur", "Mesures", "TR%", "Signe"].map((h) => (
                                                <th key={h} style={{ padding: "8px 10px", textAlign: "left", fontSize: 10, textTransform: "uppercase", letterSpacing: ".04em", color: T.faint }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(kpiComp[date] || []).map((r) => (
                                            <tr key={r.operateur} style={{ borderBottom: `1px solid ${T.lineSoft}` }}>
                                                <td style={{ padding: "8px 10px", fontWeight: 700, color: T.ink }}>{r.operateur}</td>
                                                <td style={{ padding: "8px 10px", fontFamily: T.mono }}>{r.total_mesures}</td>
                                                <td style={{ padding: "8px 10px", fontWeight: 800, fontFamily: T.mono, color: r.tr_pct >= 95 ? T.green : T.amber }}>{r.tr_pct}%</td>
                                                <td style={{ padding: "8px 10px", fontFamily: T.mono, fontWeight: 700 }}>{r.signe}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ))}
                    </div>
                </Card>
            )}
            {compare.length === 2 && !(kpiComp[compare[0]] && kpiComp[compare[1]]) && <Loading label="Chargement de la comparaison…" />}
            {compare.length < 2 && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 13, color: T.faint, padding: "10px 0", fontWeight: 600 }}>
                    <Icon d={Ico.folders} size={16} color={T.faint} />Sélectionnez 2 campagnes pour les comparer côte à côte
                </div>
            )}
        </div>
    );
}

/* ─────────────────────────────────────────────────────────
   7. ETL & Logs
   ───────────────────────────────────────────────────────── */
function SectionETL({ token }) {
    const [log, setLog] = useState(null);
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);
    const [search, setSearch] = useState("");

    const chargerLog = useCallback(() => { apiFetch("/api/admin/etl-log", token).then(setLog).catch(() => { }); }, [token]);
    useEffect(() => { chargerLog(); }, [chargerLog]);

    async function relancer() {
        setRunning(true); setResult(null);
        const res = await apiFetch("/api/admin/relancer-etl", token, { method: "POST" });
        setResult(res); setRunning(false); chargerLog();
    }

    const lignes = (log?.contenu || "").split("\n").filter((l) => !search || l.toLowerCase().includes(search.toLowerCase()));

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <Field icon={Ico.search} flex={1} minWidth={200} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filtrer les logs…" />
                <Btn small variant="outline" color={T.navy} onClick={chargerLog}><Icon d={Ico.refresh} size={14} />Actualiser</Btn>
                <Btn small color={T.green} onClick={relancer} loading={running}><Icon d={Ico.play} size={13} color="#fff" />Relancer ETL</Btn>
            </div>

            {log && (
                <div style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 12.5, color: T.sub }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: T.green, boxShadow: `0 0 0 3px ${T.green}22` }} />
                    <b style={{ color: T.ink, fontFamily: T.mono }}>{log.total_lignes}</b> lignes · {lignes.length} affichées{search && ` · filtre : "${search}"`}
                </div>
            )}

            {result && (
                <Alert type={result.success ? "success" : "error"}>
                    {result.success ? "Pipeline ETL exécuté avec succès" : `Erreur : ${result.error || result.stderr}`}
                    {result.stdout && <pre style={{ fontSize: 11, marginTop: 8, marginBottom: 0, whiteSpace: "pre-wrap", fontFamily: T.mono, color: T.sub }}>{result.stdout}</pre>}
                </Alert>
            )}

            <div style={{ background: "#0B1220", borderRadius: 12, border: "1px solid #1B2740", overflow: "hidden" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 14px", borderBottom: "1px solid #1B2740", background: "#0E1729" }}>
                    <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#FF5F57" }} />
                    <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#FEBC2E" }} />
                    <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#28C840" }} />
                    <span style={{ marginLeft: 8, fontSize: 11.5, color: "#7D8AA3", fontFamily: T.mono }}>etl.log</span>
                </div>
                <div style={{ padding: 16, fontFamily: T.mono, fontSize: 12, maxHeight: 450, overflowY: "auto", lineHeight: 1.75 }}>
                    {lignes.length > 0 ? lignes.map((line, i) => (
                        <div key={i} style={{
                            display: "flex", gap: 14,
                            color: /ERREUR|Error/.test(line) ? "#FF7B72" : /succès|OK|✅/.test(line) ? "#7EE787" : /\[ETL\]/.test(line) ? "#79C0FF" : "#C9D4E5",
                        }}>
                            <span style={{ color: "#3E4C66", userSelect: "none", minWidth: 22, textAlign: "right" }}>{i + 1}</span>
                            <span style={{ whiteSpace: "pre-wrap" }}>{line || " "}</span>
                        </div>
                    )) : <span style={{ color: "#7D8AA3" }}>Aucun log disponible</span>}
                </div>
            </div>
        </div>
    );
}

/* ─────────────────────────────────────────────────────────
   8. Carte GPS (Leaflet vanilla)
   ───────────────────────────────────────────────────────── */
function SectionCarte({ token }) {
    const [points, setPoints] = useState([]);
    const [anomaliesML, setAnomaliesML] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [filtre, setFiltre] = useState("tous");
    const mapEl = React.useRef(null);
    const mapObj = React.useRef(null);
    const layer = React.useRef(null);

    useEffect(() => {
        Promise.all([
            apiFetch("/api/carte/mesures", token),
            apiFetch("/api/ml/anomalies", token).catch(() => ({ results: [] })),  // ← nouveau
        ]).then(([carte, anom]) => {
            setPoints(carte.results || []);
            // Construit un Set des numéros de mesure détectés comme anomalies
            const idsAnom = new Set(
                (anom.results || [])
                    .filter((a) => a.est_anomalie === 1)
                    .map((a) => String(a.mesure))
            );
            setAnomaliesML(idsAnom);
        }).catch(() => { }).finally(() => setLoading(false));
    }, [token]);

    const classe = (d) => (!d ? { label: "?", color: T.faint } : d <= 5 ? { label: "TE", color: T.red } : d < 120 ? { label: "TC", color: T.amber } : { label: "TR", color: T.green });
    const isAnom = (p) => anomaliesML.has(String(p.mesure));
    const operateurs = [...new Set(points.map((p) => p.operateur).filter(Boolean))];
    const filtres = filtre === "tous" ? points : filtre === "anomalies" ? points.filter(isAnom) : points.filter((p) => p.operateur === filtre);

    const statsOp = {};
    points.forEach((p) => {
        const op = p.operateur || "Inconnu";
        (statsOp[op] = statsOp[op] || { total: 0, TR: 0, TC: 0, TE: 0 }).total++;
        statsOp[op][classe(parseFloat(p.duree || 0)).label]++;
    });

    // init + refresh markers
    useEffect(() => {
        if (loading || !points.length) return;
        let cancelled = false;
        ensureLeaflet().then((L) => {
            if (cancelled || !L || !mapEl.current) return;
            if (!mapObj.current) {
                mapObj.current = L.map(mapEl.current, { scrollWheelZoom: false }).setView([31.7917, -7.0926], 6);
                L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap contributors", maxZoom: 19 }).addTo(mapObj.current);
            }
            if (layer.current) layer.current.remove();
            layer.current = L.layerGroup().addTo(mapObj.current);
            filtres.forEach((p) => {
                if (!p.latitude || !p.longitude) return;
                const cl = classe(parseFloat(p.duree || 0));
                const anom = isAnom(p);
                L.circleMarker([p.latitude, p.longitude], {
                    radius: anom ? 9 : 6.5, color: anom ? T.red : opColor(p.operateur),
                    fillColor: cl.color, fillOpacity: 0.85, weight: anom ? 2.5 : 1.5,
                }).bindPopup(
                    `<div style="font-family:sans-serif;font-size:13px;line-height:1.6;min-width:190px">
            <div style="font-weight:800;color:${opColor(p.operateur)};font-size:14px;margin-bottom:5px">${p.operateur}${anom ? ' <span style="color:' + T.red + ';font-size:11px">⚠ Anomalie</span>' : ""}</div>
            <div><b>Mesure :</b> n°${p.mesure}</div>
            <div><b>Ville :</b> ${p.site_emission}</div>
            <div><b>Durée :</b> ${p.duree}s <span style="background:${cl.color};color:#fff;font-size:10px;font-weight:700;padding:1px 6px;border-radius:4px;margin-left:4px">${cl.label}</span></div>
            <div><b>Signal :</b> ${p.signal_strength} dBm</div>
            <div><b>PESQ :</b> ${p.quality_pesq || "—"}</div>
            <div><b>Date :</b> ${p.date_mesure}</div>
          </div>`
                ).addTo(layer.current);
            });
        });
        return () => { cancelled = true; };
    }, [loading, points, filtre, anomaliesML]);

    useEffect(() => () => { if (mapObj.current) { mapObj.current.remove(); mapObj.current = null; } }, []);

    if (loading) return <Loading label="Chargement de la carte…" />;
    if (!points.length) return <div style={{ color: T.faint, padding: 20, fontFamily: T.sans, fontSize: 13.5 }}>Aucune mesure géolocalisée disponible.</div>;

    const chip = (id, label) => (
        <button key={id} onClick={() => setFiltre(id)} style={{
            padding: "7px 14px", borderRadius: 999, cursor: "pointer", fontFamily: T.sans, fontSize: 12.5, fontWeight: 700,
            border: `1.5px solid ${filtre === id ? T.blue : T.line}`, background: filtre === id ? `${T.blue}12` : T.card, color: filtre === id ? T.blue : T.sub,
            transition: "all .15s",
        }}>{label}</button>
    );

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* stats GPS — style ligne d'icônes compacte */}
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(operateurs.length + 1, 4)},1fr)`, gap: 12 }}>
                <div style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: 14, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ width: 38, height: 38, borderRadius: 10, background: `${T.blue}12`, color: T.blue, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Icon d={Ico.pin} size={18} color={T.blue} />
                    </span>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 19, fontWeight: 800, fontFamily: T.mono, color: T.ink, lineHeight: 1.1 }}>{points.length}</div>
                        <div style={{ fontSize: 11.5, color: T.sub, marginTop: 2, fontWeight: 600 }}>Points GPS</div>
                    </div>
                </div>
                {Object.entries(statsOp).map(([op, s]) => (
                    <div key={op} style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: 14, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                        <span style={{ width: 38, height: 38, borderRadius: 10, background: `${opColor(op)}14`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <span style={{ width: 10, height: 10, borderRadius: "50%", background: opColor(op) }} />
                        </span>
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 19, fontWeight: 800, fontFamily: T.mono, color: T.ink, lineHeight: 1.1 }}>{s.total}</div>
                            <div style={{ fontSize: 11.5, color: T.sub, marginTop: 2, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{op}</div>
                            <div style={{ display: "flex", gap: 7, marginTop: 4, fontSize: 10, fontWeight: 800, fontFamily: T.mono }}>
                                <span style={{ color: T.green }}>TR {s.TR}</span><span style={{ color: T.amber }}>TC {s.TC}</span><span style={{ color: T.red }}>TE {s.TE}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* carte, dans une Card avec en-tête et légende intégrée */}
            <Card style={{ padding: "18px 20px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ width: 3, height: 15, background: T.red, borderRadius: 2 }} />
                        <span style={{ fontSize: 12.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".05em", color: T.ink }}>Localisation des mesures</span>
                    </div>
                    <span style={{ fontSize: 12, color: T.faint }}>{filtres.length} point{filtres.length > 1 ? "s" : ""} affiché{filtres.length > 1 ? "s" : ""}</span>
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
                    {chip("tous", `Tous (${points.length})`)}
                    {chip("anomalies", "⚠ Anomalies")}
                    {operateurs.map((op) => chip(op, `${op} (${statsOp[op]?.total || 0})`))}
                </div>

                <div ref={mapEl} style={{ height: 440, borderRadius: 12, overflow: "hidden", border: `1px solid ${T.line}`, zIndex: 0, background: T.cardAlt }} />

                <div style={{ display: "flex", gap: 18, flexWrap: "wrap", fontSize: 11.5, fontWeight: 600, color: T.sub, marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.lineSoft}` }}>
                    {operateurs.map((op) => (
                        <span key={op} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <span style={{ width: 9, height: 9, borderRadius: "50%", background: opColor(op) }} />{op}
                        </span>
                    ))}
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <span style={{ width: 10, height: 10, borderRadius: "50%", border: `2px solid ${T.red}` }} />Anomalie
                    </span>
                </div>
            </Card>
        </div>
    );
}

/* ─────────────────────────────────────────────────────────
   9. Ajouter / Modifier une mesure
   ───────────────────────────────────────────────────────── */
function SectionAjouterMesure({ token }) {
    const vide = {
        service: "4G", operateur: "", site_emission: "", site_reception: "", duree: "", signal_strength: "", min_signal: "", max_signal: "",
        avg_signal: "", quality_pesq: "", date_mesure: "", heure_gps: "", latitude: "", longitude: "", cid: "", lac: "",
        num_telephone: "", operateur_appellant: "", num_appellant: "", config: "1",
    };
    const [form, setForm] = useState(vide);
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState(null);
    const [editId, setEditId] = useState(null);
    const [search, setSearch] = useState("");
    const [mesures, setMesures] = useState([]);

    const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    useEffect(() => { apiFetch("/api/admin/mesures?page=1&per_page=50", token).then((d) => setMesures(d.mesures || [])).catch(() => { }); }, [token, msg]);

    async function soumettre() {
        setLoading(true); setMsg(null);
        try {
            const endpoint = editId ? `/api/admin/mesures/${editId}` : "/api/admin/mesures/ajouter";
            const res = await apiFetch(endpoint, token, { method: editId ? "PUT" : "POST", body: JSON.stringify(form) });
            if (res.success) { setMsg({ type: "success", text: res.message }); setForm(vide); setEditId(null); }
            else setMsg({ type: "error", text: res.error });
        } catch { setMsg({ type: "error", text: "Erreur serveur." }); }
        setLoading(false);
    }

    function chargerPourEdit(m) {
        setEditId(m.id);
        setForm({
            service: m.service || "4G", operateur: m.operateur || "", site_emission: m.site_emission || "", site_reception: m.site_emission || "",
            duree: m.duree || "", signal_strength: m.signal_strength || "", min_signal: m.signal_strength || "", max_signal: m.signal_strength || "",
            avg_signal: m.signal_strength || "", quality_pesq: m.quality_pesq || "", date_mesure: m.date_mesure || "", heure_gps: m.heure_gps || "",
            latitude: m.latitude || "", longitude: m.longitude || "", cid: m.cid || "", lac: m.lac || "",
            num_telephone: m.num_telephone || "", operateur_appellant: m.operateur_appellant || "", num_appellant: m.num_appellant || "", config: "1",
        });
        window.scrollTo(0, 0);
    }

    const QUALITE = [
        // 5- Parfait : score >= 4.0
        "5- Parfaite (4,50)", "5- Parfaite (4,40)", "5- Parfaite (4,30)",
        "5- Parfaite (4,20)", "5- Parfaite (4,10)", "5- Parfaite (4,00)",
        // 4- Acceptable : score >= 3.5
        "4- Acceptable (3,90)", "4- Acceptable (3,80)", "4- Acceptable (3,70)",
        "4- Acceptable (3,60)", "4- Acceptable (3,50)",
        // 3- Médiocre : score >= 3.0
        "3- Médiocre (3,40)", "3- Médiocre (3,30)", "3- Médiocre (3,20)",
        "3- Médiocre (3,10)", "3- Médiocre (3,00)",
        // 2- Mauvaise : score >= 2.0
        "2- Mauvaise (2,90)", "2- Mauvaise (2,70)", "2- Mauvaise (2,50)",
        "2- Mauvaise (2,30)", "2- Mauvaise (2,10)", "2- Mauvaise (2,00)",
        // 1- Très Mauvaise : score < 2.0
        "1-  Mauvaise (1,80)", "1-  Mauvaise (1,50)",
        "1-  Mauvaise (1,20)", "1-  Mauvaise (1,00)",
        "1-  Mauvaise (0,50)", "1-  Mauvaise (-0,50)",
    ];
    const OPS_SEL = ["Opérateur 1", "Opérateur 2", "Opérateur 3"];

    const groupes = [
        {
            titre: "Informations réseau", icon: Ico.signal, champs: [
                { k: "service", label: "Technologie", type: "select", opts: ["2G", "3G", "4G", "5G"], requis: true },
                { k: "operateur", label: "Opérateur", type: "select", opts: OPS_SEL, requis: true },
                { k: "site_emission", label: "Ville émission", type: "text", ph: "Ex : Rabat", requis: true },
                { k: "site_reception", label: "Ville réception", type: "text", ph: "Ex : Rabat" },
            ]
        },
        {
            titre: "Numéros de téléphone", icon: Ico.dots, champs: [
                { k: "num_telephone", label: "N° appelé", type: "text", ph: "0697102503" },
                { k: "operateur_appellant", label: "Opérateur appelant", type: "select", opts: OPS_SEL },
                { k: "num_appellant", label: "N° appelant", type: "text", ph: "0656708994" },
            ]
        },
        {
            titre: "Mesures temporelles", icon: Ico.clock, champs: [
                { k: "date_mesure", label: "Date (jj-mm-aaaa)", type: "text", ph: "11-07-2026", requis: true },
                { k: "heure_gps", label: "Heure GPS", type: "text", ph: "10:30:00" },
                { k: "duree", label: "Durée (secondes)", type: "number", ph: "120", requis: true },
            ]
        },
        {
            titre: "Signal radio", icon: Ico.signal, champs: [
                { k: "signal_strength", label: "Signal (dBm)", type: "number", ph: "-65", requis: true },
                { k: "min_signal", label: "Signal min (dBm)", type: "number", ph: "-80" },
                { k: "max_signal", label: "Signal max (dBm)", type: "number", ph: "-55" },
                { k: "avg_signal", label: "Signal moyen (dBm)", type: "number", ph: "-67" },
                { k: "cid", label: "CID (Cell ID)", type: "text", ph: "30540" },
                { k: "lac", label: "LAC", type: "text", ph: "31200" },
            ]
        },
        {
            titre: "Qualité vocale PESQ", icon: Ico.chart, champs: [
                { k: "quality_pesq", label: "Qualité PESQ", type: "select", opts: QUALITE, requis: true },
            ]
        },
        {
            titre: "Localisation GPS", icon: Ico.pin, champs: [
                { k: "latitude", label: "Latitude", type: "text", ph: "34.016666" },
                { k: "longitude", label: "Longitude", type: "text", ph: "-6.816666" },
            ]
        },
    ];

    const inputStyle = (requis, val) => ({
        width: "100%", padding: "9px 12px", borderRadius: 9, fontSize: 13, fontFamily: T.sans, color: T.ink,
        border: `1.5px solid ${requis && !val ? T.red : T.line}`, boxSizing: "border-box", outline: "none",
    });
    const mFiltered = mesures.filter((m) => !search || String(m.mesure).includes(search) || (m.operateur || "").toLowerCase().includes(search.toLowerCase()) || (m.site_emission || "").toLowerCase().includes(search.toLowerCase()));

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <Card style={{ padding: 22 }}>
                <SectionTitle>{editId ? `Modifier mesure id=${editId}` : "Ajouter une mesure"}</SectionTitle>
                {msg && <div style={{ marginBottom: 14 }}><Alert type={msg.type}>{msg.text}</Alert></div>}

                <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
                    {groupes.map((groupe) => (
                        <div key={groupe.titre}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 700, color: T.navy, marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${T.line}` }}>
                                <Icon d={groupe.icon} size={14} color={T.navy} />{groupe.titre}
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14 }}>
                                {groupe.champs.map((f) => (
                                    <div key={f.k}>
                                        <label style={{ fontSize: 11, fontWeight: 700, color: T.faint, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: 6 }}>
                                            {f.label}{f.requis && <span style={{ color: T.red, marginLeft: 3 }}>*</span>}
                                        </label>
                                        {f.type === "select" ? (
                                            <select value={form[f.k]} onChange={upd(f.k)} style={{ ...inputStyle(f.requis, form[f.k]), background: T.card }}>
                                                <option value="">— Choisir —</option>
                                                {f.opts.map((o) => <option key={o} value={o}>{o}</option>)}
                                            </select>
                                        ) : (
                                            <input type={f.type} value={form[f.k]} onChange={upd(f.k)} placeholder={f.ph || ""} style={inputStyle(f.requis, form[f.k])} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <div style={{ display: "flex", gap: 10, marginTop: 22, alignItems: "center" }}>
                    <Btn onClick={soumettre} loading={loading} color={editId ? T.amber : T.green}>
                        <Icon d={editId ? Ico.edit : Ico.plus} size={16} color="#fff" />{editId ? "Enregistrer les modifications" : "Ajouter la mesure"}
                    </Btn>
                    {editId && <Btn variant="outline" color={T.sub} onClick={() => { setForm(vide); setEditId(null); setMsg(null); }}>Annuler</Btn>}
                    <span style={{ fontSize: 11.5, color: T.faint, marginLeft: 4 }}><span style={{ color: T.red }}>*</span> Champs obligatoires</span>
                </div>
            </Card>

            <Card style={{ padding: 22 }}>
                <SectionTitle>Mesures existantes — cliquer pour modifier</SectionTitle>
                <div style={{ marginBottom: 14 }}><Field icon={Ico.search} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher…" /></div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {mFiltered.slice(0, 20).map((m) => (
                        <div key={m.id} onClick={() => chargerPourEdit(m)} style={{
                            display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 15px",
                            borderRadius: 10, border: `1px solid ${T.line}`, cursor: "pointer",
                            background: editId === m.id ? T.cardAlt : T.card, transition: "background .15s",
                        }}>
                            <div style={{ fontSize: 13 }}>
                                <span style={{ fontWeight: 700, fontFamily: T.mono }}>N°{m.mesure}</span>
                                <span style={{ color: T.faint, margin: "0 8px" }}>·</span>
                                <span style={{ fontWeight: 700, color: opColor(m.operateur) }}>{m.operateur}</span>
                                <span style={{ color: T.faint, margin: "0 8px" }}>·</span>
                                <span>{m.site_emission}</span>
                            </div>
                            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                                <span style={{ fontFamily: T.mono, fontSize: 12, color: T.sub }}>{m.duree}s · {m.signal_strength}dBm</span>
                                <span style={{ fontSize: 11.5, color: T.navy, fontWeight: 700 }}>{m.date_mesure}</span>
                                <Icon d={Ico.edit} size={14} color={T.blue} />
                            </div>
                        </div>
                    ))}
                    {mFiltered.length > 20 && <div style={{ fontSize: 12, color: T.faint, textAlign: "center", padding: 8 }}>{mFiltered.length - 20} mesure(s) supplémentaire(s) — affinez la recherche</div>}
                </div>
            </Card>
        </div>
    );
}

/* ─────────────────────────────────────────────────────────
   10. Chargement de données (Import CSV)
   ───────────────────────────────────────────────────────── */
function SectionImport({ token }) {
    const [fichier, setFichier] = useState(null);
    const [apercu, setApercu] = useState([]);
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState(null);
    const [contenu, setContenu] = useState("");

    function lireFichier(e) {
        const f = e.target.files[0]; if (!f) return;
        setFichier(f);
        const reader = new FileReader();
        reader.onload = (ev) => { const texte = ev.target.result; setContenu(texte); setApercu(texte.split("\n").slice(0, 6)); };
        reader.readAsText(f, "UTF-8");
    }

    async function importer() {
        if (!contenu) return;
        setLoading(true); setMsg(null);
        try {
            const res = await apiFetch("/api/admin/import-csv", token, { method: "POST", body: JSON.stringify({ contenu }) });
            if (res.success) setMsg({ type: "success", text: `${res.inseres} mesure(s) importée(s).${res.erreurs?.length ? ` ${res.erreurs.length} erreur(s).` : ""}` });
            else setMsg({ type: "error", text: res.error });
        } catch { setMsg({ type: "error", text: "Erreur lors de l'import." }); }
        setLoading(false);
    }

    function telechargerTemplate() {
        const header = "mesure;service;signal_strength;min_signal;max_signal;avg_signal;site_emission;site_reception;date_mesure;operateur;num_telephone;operateur_appellant;num_appellant;config;heure_gps;latitude;longitude;cid;lac;duree;quality_pesq\n";
        const exemple = "1;4G;-65;-78;-55;-66.0;Rabat;Rabat;05-07-2026;Orange MA;0656708994;Maroc Telecom;0697102503;1;10:30:00;34.016666;-6.816666;30540;31200;125;5- Parfaite (4,32)\n";
        const blob = new Blob(["\uFEFF" + header + exemple], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob); const a = document.createElement("a");
        a.href = url; a.download = "template_mesures_qos.csv"; a.click(); URL.revokeObjectURL(url);
    }

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <Card style={{ padding: 24 }}>
                <SectionTitle>Importer un fichier CSV</SectionTitle>
                <div style={{ background: T.cardAlt, border: `1px solid ${T.line}`, borderRadius: 10, padding: "14px 18px", marginBottom: 20, fontSize: 12, fontFamily: T.mono, color: T.ink, lineHeight: 1.7 }}>
                    <div style={{ fontWeight: 700, marginBottom: 6, fontFamily: T.sans }}>Format CSV attendu (séparateur : point-virgule) :</div>
                    mesure;service;signal_strength;min_signal;max_signal;avg_signal;<br />
                    site_emission;site_reception;date_mesure;operateur;num_telephone;<br />
                    operateur_appellant;num_appellant;config;heure_gps;latitude;longitude;cid;lac;duree;quality_pesq
                </div>

                <label style={{
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 10, cursor: "pointer",
                    border: `2px dashed ${T.line}`, borderRadius: 12, padding: "32px", marginBottom: 16,
                    background: fichier ? `${T.green}0A` : T.cardAlt,
                }}>
                    <span style={{ width: 44, height: 44, borderRadius: 12, background: `${T.navy}12`, color: T.navy, display: "flex", alignItems: "center", justifyContent: "center" }}><Icon d={Ico.upload} size={21} color={T.navy} /></span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>{fichier ? fichier.name : "Cliquez pour choisir un fichier CSV"}</span>
                    {!fichier && <span style={{ fontSize: 12, color: T.faint }}>Formats acceptés : .csv, .txt (encodage UTF-8)</span>}
                    <input type="file" accept=".csv,.txt" onChange={lireFichier} style={{ display: "none" }} />
                </label>

                {apercu.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: T.sub, marginBottom: 8 }}>Aperçu (5 premières lignes) :</div>
                        <div style={{ background: "#0B1220", borderRadius: 10, padding: "12px 16px", fontFamily: T.mono, fontSize: 11, color: "#C9D4E5", overflowX: "auto" }}>
                            {apercu.map((l, i) => <div key={i}>{l}</div>)}
                        </div>
                    </div>
                )}
                {msg && <div style={{ marginBottom: 14 }}><Alert type={msg.type}>{msg.text}</Alert></div>}
                <Btn onClick={importer} disabled={!fichier} loading={loading} color={T.green}><Icon d={Ico.upload} size={16} color="#fff" />Importer les données</Btn>
            </Card>

            <Card style={{ padding: 22 }}>
                <SectionTitle>Télécharger un template CSV</SectionTitle>
                <div style={{ fontSize: 13, color: T.sub, marginBottom: 14, lineHeight: 1.6 }}>Téléchargez un fichier CSV vide avec les bonnes colonnes pour préparer vos données à importer.</div>
                <Btn variant="outline" color={T.navy} onClick={telechargerTemplate}><Icon d={Ico.download} size={15} />Télécharger template.csv</Btn>
            </Card>
        </div>
    );
}

/* ─────────────────────────────────────────────────────────
   11. Gestion des campagnes (Ajouter / Modifier / Supprimer)
   ───────────────────────────────────────────────────────── */
function SectionGestionCampagnes({ token }) {
    const [campagnes, setCampagnes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [msg, setMsg] = useState(null);
    const [modifier, setModifier] = useState({});
    const [confirm, setConfirm] = useState(null);

    function charger() {
        setLoading(true);
        apiFetch("/api/admin/campagnes", token).then((d) => setCampagnes(d.campagnes || [])).catch(() => { }).finally(() => setLoading(false));
    }
    useEffect(() => { charger(); }, [token]);

    async function modifierDate(ancienne, nouvelle) {
        try {
            const res = await apiFetch(`/api/admin/campagnes/${ancienne}`, token, { method: "PUT", body: JSON.stringify({ nouvelle_date: nouvelle }) });
            if (res.success) { setMsg({ type: "success", text: res.message }); setModifier({}); charger(); }
            else setMsg({ type: "error", text: res.error });
        } catch { setMsg({ type: "error", text: "Erreur serveur." }); }
    }

    async function supprimerCampagne(date) {
        if (confirm !== date) { setConfirm(date); return; }
        try {
            const res = await apiFetch(`/api/admin/campagnes/${date}`, token, { method: "DELETE" });
            if (res.success) { setMsg({ type: "success", text: res.message }); setConfirm(null); charger(); }
            else setMsg({ type: "error", text: res.error });
        } catch { setMsg({ type: "error", text: "Erreur serveur." }); }
    }

    if (loading) return <Loading />;

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {msg && <Alert type={msg.type}>{msg.text}</Alert>}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 13, color: T.sub }}><b style={{ color: T.ink, fontFamily: T.mono }}>{campagnes.length}</b> campagne(s) enregistrée(s)</span>
                <Btn small variant="outline" color={T.navy} onClick={charger}><Icon d={Ico.refresh} size={14} />Actualiser</Btn>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {campagnes.map((c, i) => (
                    <Card key={c.date_mesure} style={{ padding: "16px 20px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                            <div style={{ flex: 1, minWidth: 200 }}>
                                <div style={{ fontWeight: 800, color: T.ink, fontSize: 14 }}>Campagne {campagnes.length - i} — {c.date_mesure}</div>
                                <div style={{ display: "flex", gap: 16, marginTop: 6, fontSize: 12, color: T.sub }}>
                                    <span>{c.nb_mesures} mesure(s)</span><span>{c.nb_operateurs} opérateur(s)</span><span>{c.nb_villes} ville(s)</span>
                                </div>
                            </div>

                            {modifier[c.date_mesure] ? (
                                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                    <input type="text" defaultValue={c.date_mesure} id={`date_${c.date_mesure}`} placeholder="dd-mm-yyyy"
                                        style={{ padding: "8px 11px", borderRadius: 8, border: `1.5px solid ${T.line}`, fontSize: 13, width: 130, outline: "none", fontFamily: T.sans }} />
                                    <Btn small color={T.green} onClick={() => modifierDate(c.date_mesure, document.getElementById(`date_${c.date_mesure}`).value)}><Icon d={Ico.check} size={14} color="#fff" />Valider</Btn>
                                    <Btn small variant="outline" color={T.sub} onClick={() => setModifier((m) => ({ ...m, [c.date_mesure]: false }))}>Annuler</Btn>
                                </div>
                            ) : (
                                <div style={{ display: "flex", gap: 8 }}>
                                    <Btn small variant="outline" color={T.navy} onClick={() => setModifier((m) => ({ ...m, [c.date_mesure]: true }))}><Icon d={Ico.edit} size={13} />Modifier</Btn>
                                    <Btn small variant={confirm === c.date_mesure ? "solid" : "outline"} color={T.red} onClick={() => supprimerCampagne(c.date_mesure)}>
                                        <Icon d={Ico.trash} size={13} color={confirm === c.date_mesure ? "#fff" : T.red} />{confirm === c.date_mesure ? "Confirmer" : "Supprimer"}
                                    </Btn>
                                </div>
                            )}
                        </div>
                    </Card>
                ))}
            </div>
        </div>
    );
}

/* ─────────────────────────────────────────────────────────
   12. Paramètres de la plateforme
   ───────────────────────────────────────────────────────── */
function SectionParams({ token }) {
    const [params, setParams] = useState({});
    const [loading, setLoading] = useState(true);
    const [msg, setMsg] = useState(null);
    const [mdp, setMdp] = useState({ ancien: "", nouveau: "", conf: "" });
    const [mdpMsg, setMdpMsg] = useState(null);

    useEffect(() => { apiFetch("/api/admin/params", token).then((d) => setParams(d.params || {})).catch(() => { }).finally(() => setLoading(false)); }, [token]);

    async function sauvegarder(cle, valeur) {
        try {
            await apiFetch(`/api/admin/params/${cle}`, token, { method: "PUT", body: JSON.stringify({ valeur }) });
            setMsg({ type: "success", text: `Paramètre « ${cle} » sauvegardé.` });
            setTimeout(() => setMsg(null), 3000);
        } catch { setMsg({ type: "error", text: "Erreur lors de la sauvegarde." }); }
    }

    async function changerMdp() {
        if (mdp.nouveau !== mdp.conf) { setMdpMsg({ type: "error", text: "Les mots de passe ne correspondent pas." }); return; }
        try {
            const res = await apiFetch("/api/admin/changer-mdp", token, { method: "POST", body: JSON.stringify({ ancien_mdp: mdp.ancien, nouveau_mdp: mdp.nouveau }) });
            if (res.success) setMdpMsg({ type: "success", text: res.message });
            else setMdpMsg({ type: "error", text: res.error });
        } catch { setMdpMsg({ type: "error", text: "Erreur serveur." }); }
    }

    if (loading) return <Loading />;

    const labels = {
        seuil_tr_min: { label: "Seuil TR% minimum (%)", type: "number" },
        seuil_te_max: { label: "Seuil TE% maximum (%)", type: "number" },
        contamination_ml: { label: "Contamination ML (0 à 1)", type: "number" },
        objectif_mesures: { label: "Objectif de collecte (nb mesures)", type: "number" },
        nom_plateforme: { label: "Nom de la plateforme", type: "text" },
        seuil_signal: { label: "Seuil signal faible (dBm)", type: "number" },
    };
    const inputStyle = { flex: 1, padding: "9px 12px", borderRadius: 9, border: `1.5px solid ${T.line}`, fontSize: 13, outline: "none", fontFamily: T.sans };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            {msg && <Alert type={msg.type}>{msg.text}</Alert>}

            <div>
                <SectionTitle>Paramètres système</SectionTitle>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                    {Object.entries(params).map(([cle, p]) => {
                        const info = labels[cle] || { label: cle, type: "text" };
                        return (
                            <Card key={cle} style={{ padding: "16px 20px" }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: T.faint, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 8 }}>{info.label}</div>
                                <div style={{ fontSize: 11.5, color: T.sub, marginBottom: 10, fontStyle: "italic" }}>{p.description}</div>
                                <div style={{ display: "flex", gap: 8 }}>
                                    <input type={info.type} defaultValue={p.valeur} id={`param_${cle}`} style={inputStyle} />
                                    <Btn small color={T.green} onClick={() => sauvegarder(cle, document.getElementById(`param_${cle}`).value)}><Icon d={Ico.check} size={14} color="#fff" /></Btn>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            </div>

            <div>
                <SectionTitle>Changer le mot de passe admin</SectionTitle>
                <Card style={{ padding: "20px 24px", maxWidth: 480 }}>
                    {mdpMsg && <div style={{ marginBottom: 14 }}><Alert type={mdpMsg.type}>{mdpMsg.text}</Alert></div>}
                    <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
                        {[{ label: "Ancien mot de passe", key: "ancien" }, { label: "Nouveau mot de passe", key: "nouveau" }, { label: "Confirmer le nouveau", key: "conf" }].map((f) => (
                            <div key={f.key}>
                                <label style={{ fontSize: 11, fontWeight: 700, color: T.faint, textTransform: "uppercase", display: "block", marginBottom: 6 }}>{f.label}</label>
                                <input type="password" value={mdp[f.key]} onChange={(e) => setMdp((p) => ({ ...p, [f.key]: e.target.value }))} style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} />
                            </div>
                        ))}
                        <div><Btn onClick={changerMdp} disabled={!mdp.ancien || !mdp.nouveau} color={T.navy}><Icon d={Ico.lock} size={15} color="#fff" />Changer le mot de passe</Btn></div>
                    </div>
                </Card>
            </div>
        </div>
    );
}

/* ─────────────────────────────────────────────────────────
   Shell
   ───────────────────────────────────────────────────────── */
function AdminPage({ token, onLogout }) {
    const [section, setSection] = useState("stats");
    useEffect(ensureAssets, []);

    const nav = [
        { id: "stats", label: "Tableau de bord", icon: Ico.dash, comp: SectionStats, desc: "Vue d'ensemble des données collectées." },
        { id: "graphes", label: "Graphiques", icon: Ico.chart, comp: SectionGraphiques, desc: "Évolution, réussite et qualité en visuels." },
        { id: "mesures", label: "Mesures", icon: Ico.table, comp: SectionMesures, desc: "Détail et gestion des mesures QoS." },
        { id: "ajouter", label: "Ajouter / Modifier", icon: Ico.plus, comp: SectionAjouterMesure, desc: "Saisie manuelle d'une mesure." },
        { id: "carte", label: "Carte GPS", icon: Ico.pin, comp: SectionCarte, desc: "Localisation des mesures terrain." },
        { id: "campagnes_g", label: "Gestion campagnes", icon: Ico.folders, comp: SectionGestionCampagnes, desc: "Modifier ou supprimer une campagne." },
        { id: "import", label: "Chargement données", icon: Ico.upload, comp: SectionImport, desc: "Import CSV et template." },
        { id: "export", label: "Export", icon: Ico.download, comp: SectionExport, desc: "Téléchargement CSV et rapports de synthèse." },
        { id: "alertes", label: "Alertes", icon: Ico.bell, comp: SectionAlertes, desc: "Détection automatique des anomalies." },
        { id: "campagnes", label: "Comparer campagnes", icon: Ico.folders, comp: SectionCampagnes, desc: "Comparaison des campagnes de mesure." },
        { id: "params", label: "Paramètres", icon: Ico.gear, comp: SectionParams, desc: "Configuration de la plateforme." },
        { id: "etl", label: "ETL & Logs", icon: Ico.etl, comp: SectionETL, desc: "Exécution et supervision du pipeline." },
    ];
    const active = nav.find((n) => n.id === section);
    const Active = active?.comp;

    return (
        <div style={{ fontFamily: T.sans, background: T.bg, minHeight: "100vh", display: "flex", color: T.ink }}>
            <aside style={{ width: 244, background: T.sidebar, color: "#fff", flexShrink: 0, display: "flex", flexDirection: "column", padding: "22px 16px", position: "sticky", top: 0, height: "100vh" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "0 6px 22px" }}>
                    <div style={{ width: 38, height: 38, borderRadius: 11, background: "#ffffff14", border: "1px solid #ffffff26", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Icon d={Ico.signal} size={19} color="#fff" />
                    </div>
                    <div>
                        <div style={{ fontSize: 13.5, fontWeight: 800, whiteSpace: "nowrap" }}>Plateforme QoS</div>
                        <div style={{ fontSize: 10.5, color: "#8FA3C2", fontWeight: 600, letterSpacing: ".03em" }}>ADMINISTRATION</div>
                    </div>
                </div>
                <div style={{ height: 1, background: "#ffffff14", margin: "0 6px 16px" }} />
                <nav style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    {nav.map((n) => {
                        const on = section === n.id;
                        return (
                            <button key={n.id} onClick={() => setSection(n.id)} style={{
                                display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 10,
                                cursor: "pointer", border: "none", textAlign: "left", fontFamily: T.sans, fontSize: 13.5,
                                fontWeight: on ? 700 : 600, background: on ? T.accentSoft : "transparent", color: on ? T.accent : "#A9B8D1",
                                boxShadow: on ? "inset 3px 0 0 " + T.accent : "none", transition: "background .12s",
                            }}
                                onMouseEnter={(e) => { if (!on) e.currentTarget.style.background = "#ffffff0d"; }}
                                onMouseLeave={(e) => { if (!on) e.currentTarget.style.background = "transparent"; }}>
                                <Icon d={n.icon} size={18} color={on ? T.accent : "#8FA3C2"} />{n.label}
                            </button>
                        );
                    })}
                </nav>
                <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
                    <a href="/" style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, fontSize: 12.5, fontWeight: 600, color: "#A9B8D1", textDecoration: "none" }}>← Retour à la plateforme</a>
                    <button onClick={onLogout} style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 12px", borderRadius: 10, cursor: "pointer", border: "1px solid #ffffff1f", background: "transparent", color: "#E7B4BD", fontFamily: T.sans, fontSize: 13, fontWeight: 700 }}>
                        <Icon d={Ico.logout} size={16} color="#E7B4BD" />Déconnexion
                    </button>
                </div>
            </aside>

            <main style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                <header style={{ padding: "22px 34px", borderBottom: `1px solid ${T.line}`, background: T.card, display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16 }}>
                    <div>
                        <div style={{ fontSize: 21, fontWeight: 800, letterSpacing: "-.015em", color: T.ink }}>{active?.label}</div>
                        <div style={{ fontSize: 13.5, color: T.sub, marginTop: 3 }}>{active?.desc}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "7px 13px", borderRadius: 999, background: T.lineSoft, border: `1px solid ${T.line}` }}>
                        <span style={{ width: 26, height: 26, borderRadius: "50%", background: T.navy, color: T.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, border: `1px solid ${T.accent}44` }}>AD</span>
                        <span style={{ fontSize: 12.5, fontWeight: 700, color: T.ink }}>Administrateur</span>
                    </div>
                </header>
                <div style={{ padding: "26px 34px", maxWidth: 1220, width: "100%", boxSizing: "border-box" }}>
                    {Active && <Active token={token} />}
                </div>
            </main>
        </div>
    );
}

/* ─────────────────────────────────────────────────────────
   Export
   ───────────────────────────────────────────────────────── */
export default function AdminDashboard() {
    const [token, saveToken, clearToken] = useToken();
    if (!token) return <LoginPage onLogin={saveToken} />;
    return <AdminPage token={token} onLogout={clearToken} />;
}
